import { Map } from 'ol';
import { getRenderPixel } from 'ol/render';
import { Modify, Snap } from 'ol/interaction';
import { Feature } from 'ol';
import ImageTile from 'ol/ImageTile';
import Tile from 'ol/Tile';
import { Tile as TileLayer, Vector as VectorLayer, Image as ImageLayer } from 'ol/layer';
import WebGLTileLayer from 'ol/layer/WebGLTile';
import { XYZ, Vector as VectorSource, TileWMS, ImageWMS, Cluster } from 'ol/source';
import GeoTIFF from 'ol/source/GeoTIFF';
import ImageStatic from 'ol/source/ImageStatic';
import { GeoJSON } from 'ol/format';
import { get as getProjection, transformExtent } from 'ol/proj';
import { createEmpty, extend, isEmpty } from 'ol/extent';
import { Style, Stroke, Fill, Circle, Text } from 'ol/style';
import type { Geometry } from 'ol/geom';
import type { Coordinate } from 'ol/coordinate';
import { Polygon, MultiPolygon, MultiPoint, Point } from 'ol/geom';
import { ensureProjectionsRegistered } from './ensureProjections';
import { buildCaviteSilangTileUrl } from '../constants/caviteSilangLayers';
import { buildIsabelaPhilriceTileUrl } from '../constants/isabelaPhilriceLayers';
import { buildDroneLayerTileUrl, type ResolvedDroneLayer } from '../constants/droneSites';
import {
  buildCADIDroughtTileUrl,
  CADI_DROUGHT_BBOX,
  CADI_DROUGHT_FRAMES,
  CADI_DROUGHT_LEGEND,
} from '../constants/cadiDroughtLayers';
import {
  buildPestRiskTileUrl,
  getPestRiskFrames,
  PEST_RISK_BBOX,
  PEST_RISK_LEGEND,
  type PestRiskPestKey,
} from '../constants/pestRiskLayers';
import { PLANTING_METHOD_COGS } from '../constants/plantingMethodLegend';
import { DIGISAKA_API_ORIGIN } from '../../../config/digisakaApiOrigin';
import { getMapLabelFontSizePx } from './mapDisplayScale';
import { shortPolygonLabel } from './polygonFeatureLabel';
import type { FloodDamageRasterRef } from '../../../types/floodDamage';
import { FLOOD_DAMAGE_RASTER_LEGEND } from './floodDamagePlaces';
import {
  buildCoconutCropTileUrl,
  COCONUT_CROP_BBOX,
  COCONUT_CROP_LEGEND,
} from '../constants/coconutCropLayers';
import {
  buildMaizeCropTileUrl,
  MAIZE_CROP_BBOX,
  MAIZE_CROP_LEGEND,
} from '../constants/maizeCropLayers';
import {
  buildRiceCropTileUrl,
  RICE_CROP_BBOX,
  RICE_CROP_LEGEND,
} from '../constants/riceCropLayers';
import {
  buildSugarcaneCropTileUrl,
  SUGARCANE_CROP_BBOX,
  SUGARCANE_CROP_LEGEND,
} from '../constants/sugarcaneCropLayers';
import {
  buildRiceGrowthStageTileUrl,
  RICE_GROWTH_STAGE_BBOX,
} from '../constants/riceGrowthStageLayers';
import {
  buildIrrigationTypeTileUrl,
  IRRIGATION_TYPE_BBOX,
  IRRIGATION_TYPE_NATIVE_BBOX,
} from '../constants/irrigationTypeLayers';
import {
  riskMapSymbology,
  worstFarmRiskLevel,
  type FarmRiskLevel,
} from './farmRiskStatus';
import { displayFarmName } from '../../../config/digisakaApi';

export type CropAreaLayerKey = 'maize' | 'sugarCane' | 'rice' | 'coconut';

/**
 * Isabela NDVI + ortho RGB tiles often paint nodata / outside-AOI as white/light-gray. Make those
 * pixels transparent so the basemap shows through (TiTiler may still send opaque RGB without alpha).
 */
function isabelaPhilriceRgbTileWhiteTransparent(tile: Tile, src: string): void {
  makeTilePixelsTransparent(tile, src, (r, g, b) => r > 228 && g > 228 && b > 228);
}

/**
 * Index colormap tiles (rdylgn) fill the rectangular COG footprint with one flat color where the
 * index is ~0 (empty pad). Color depends on rescale:
 *   ExG  → (253, 216, 132)
 *   NDVI → (244, 109, 67)
 *   SAVI → (248, 139, 81)
 * Only strip pixels very close to those exact pad colors. A wide orange/yellow match also removes
 * real mid-range crop pixels (same colormap family) — keep the threshold tight.
 */
function droneOrthoIndexTilePaddingTransparent(tile: Tile, src: string): void {
  const ZERO_PAD_COLORS: Array<[number, number, number]> = [
    [253, 216, 132], // ExG @ ~0
    [244, 109, 67], // NDVI @ ~0
    [248, 139, 81], // SAVI @ ~0
  ];
  // Tight: empty pad is nearly uniform; real vegetation colors vary around nearby hues.
  const PAD_DIST2 = 18 * 18;

  makeTilePixelsTransparent(tile, src, (r, g, b) => {
    if (r > 248 && g > 248 && b > 248) return true;
    for (const [pr, pg, pb] of ZERO_PAD_COLORS) {
      const dr = r - pr;
      const dg = g - pg;
      const db = b - pb;
      if (dr * dr + dg * dg + db * db <= PAD_DIST2) return true;
    }
    return false;
  });
}

function makeTilePixelsTransparent(
  tile: Tile,
  src: string,
  shouldClear: (r: number, g: number, b: number) => boolean
): void {
  const imageTile = tile as ImageTile;
  const image = imageTile.getImage();
  if (!(image instanceof HTMLImageElement)) {
    return;
  }
  image.crossOrigin = 'anonymous';
  const onLoad = () => {
    image.removeEventListener('load', onLoad);
    image.removeEventListener('error', onError);
    const w = image.naturalWidth;
    const h = image.naturalHeight;
    if (!w || !h) return;
    try {
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(image, 0, 0);
      const imageData = ctx.getImageData(0, 0, w, h);
      const d = imageData.data;
      for (let i = 0; i < d.length; i += 4) {
        if (shouldClear(d[i], d[i + 1], d[i + 2])) {
          d[i + 3] = 0;
        }
      }
      ctx.putImageData(imageData, 0, 0);
      imageTile.setImage(canvas);
    } catch {
      // Tainted canvas / CORS — keep original image
    }
  };
  const onError = () => {
    image.removeEventListener('load', onLoad);
    image.removeEventListener('error', onError);
  };
  image.addEventListener('load', onLoad);
  image.addEventListener('error', onError);
  image.src = src;
}

export interface WMSLayerConfig {
  url: string;
  layers: string;
  styles?: string;
  defaultTime: string;
  visible: boolean;
}

export interface LayerVisibilityState {
  yield: boolean;
  vhi: boolean;
  lst: boolean;
  heatMap: boolean;
  timeSeries: boolean;
  // New crop area layers
  maize: boolean;
  sugarCane: boolean;
  rice: boolean;
  coconut: boolean;
  // Drone NDVI layer
  droneNDVI: boolean;
  // Rice forecast layer
  riceForecast: boolean;
  // Black Sigatoka Risk Index layer
  bsri: boolean;
  // Straw Burn Detection layer
  strawBurn: boolean;
  // Planting Method Map layers (nationwide Philippines)
  plantingMethodDry2025: boolean;
  plantingMethodDry2024: boolean;
  // Rice crop growth stage (nationwide wet-season COG)
  riceGrowthStage: boolean;
  // Irrigation type (nationwide, band 1 of the Sep 2026 H2 stack)
  irrigationType: boolean;
  // CADI agricultural drought forecast (Titiler COG time series)
  cadiDrought: boolean;
  // Rice pest risk maps (Titiler COG quarterly time series)
  pestRisk: boolean;
  // Isabela PhilRice drone COGs (Titiler XYZ)
  isabelaPhilriceNDVI: boolean;
  isabelaPhilriceOrtho: boolean;
  // Cavite Silang drone COGs (Titiler XYZ)
  caviteSilangNDVI: boolean;
  caviteSilangOrtho: boolean;
  // Tree Detection layer
  treeDetection: boolean;
}

export interface LayerOpacityState {
  yield: number;
  vhi: number;
  lst: number;
  heatMap: number;
  timeSeries: number;
  // New crop area layers
  maize: number;
  sugarCane: number;
  rice: number;
  coconut: number;
  // Drone NDVI layer
  droneNDVI: number;
  // Rice forecast layer
  riceForecast: number;
  // Black Sigatoka Risk Index layer
  bsri: number;
  // Straw Burn Detection layer
  strawBurn: number;
  // Planting Method Map layers (nationwide Philippines)
  plantingMethodDry2025: number;
  plantingMethodDry2024: number;
  // Rice crop growth stage (nationwide wet-season COG)
  riceGrowthStage: number;
  // Irrigation type (nationwide, band 1 of the Sep 2026 H2 stack)
  irrigationType: number;
  // CADI agricultural drought forecast (Titiler COG time series)
  cadiDrought: number;
  // Rice pest risk maps (Titiler COG quarterly time series)
  pestRisk: number;
  // Isabela PhilRice drone COGs (Titiler XYZ)
  isabelaPhilriceNDVI: number;
  isabelaPhilriceOrtho: number;
  // Cavite Silang drone COGs (Titiler XYZ)
  caviteSilangNDVI: number;
  caviteSilangOrtho: number;
  // Tree Detection layer
  treeDetection: number;
}

export class MapLayerManager {
  private map: Map;
  private vectorLayer: VectorLayer | null = null;
  private heatMapLayer: TileLayer | null = null;
  private timeSeriesLayer: TileLayer | null = null;

  // WMS layers (GeoServer — Past yield / NDVI_Yield only; VHI/LST use GEE tiles via PH APIs)
  private yieldLayer: ImageLayer<ImageWMS> | null = null;

  // New crop area layers
  private maizeLayer: TileLayer | null = null;
  private sugarCaneLayer: TileLayer | null = null;
  /** Country-scale sugarcane picture. Shown below zoom 9, where the COG overviews are empty. */
  private riceLayer: TileLayer | null = null;
  private coconutLayer: TileLayer<XYZ> | null = null;

  // Rice forecast layer
  private riceForecastLayer: TileLayer | null = null;
  private riceForecastData: any = null;
  // Tree Detection layer
  private treeDetectionLayer: ImageLayer | null = null;
  private treeDetectionData: any = null;
  private floodDamageRasterLayer: WebGLTileLayer | null = null;
  private floodDamageRasterUrl: string | null = null;
  // Tree detection points (GeoJSON) layer
  private treeDetectionPointsLayer: VectorLayer | null = null;
  /** Opened farm analysis group / overview — farm polygons */
  private farmGroupLayer: VectorLayer<VectorSource> | null = null;
  /** Clustered farm identity markers (one point per farm) */
  private farmClusterLayer: VectorLayer<VectorSource> | null = null;
  /** LGU assigned monitoring area outline */
  private monitoringAreaBoundaryLayer: VectorLayer<VectorSource> | null = null;
  // Crop-field boundary delineation (polygon GeoJSON, projected CRS)
  private cropfieldDelineationLayer: VectorLayer<VectorSource> | null = null;
  private lastCropfieldGeoJson: any = null;
  private lastCropfieldCrs: string | null = null;
  private cropfieldVertexEditSource: VectorSource | null = null;
  private cropfieldVertexEditLayer: VectorLayer<VectorSource> | null = null;
  private cropfieldVertexModify: Modify | null = null;
  private cropfieldVertexSnap: Snap | null = null;
  private cropfieldVertexNeighborSnap: Snap | null = null;
  private cropfieldVertexUndoStack: Geometry[] = [];
  private cropfieldVertexRedoStack: Geometry[] = [];
  private cropfieldVertexPendingSnapshot: Geometry | null = null;
  private cropfieldVertexHistoryListener:
    | ((state: { canUndo: boolean; canRedo: boolean }) => void)
    | null = null;
  private readonly cropfieldVertexMaxHistory = 50;
  // Crown segmentation (polygon GeoJSON from Digisaka GPT tree-detection crown-segmentation)
  private crownSegmentationLayer: VectorLayer<VectorSource> | null = null;

  // Black Sigatoka Risk Index layer
  private bsriLayer: TileLayer | null = null;
  private bsriData: any = null;

  // VHI Philippines (agricultural drought) layer
  private vhiPHLayer: TileLayer | null = null;
  private vhiPHData: any = null;

  // LST Philippines (MODIS) layer
  private lstPHLayer: TileLayer | null = null;
  private lstPHData: any = null;

  // Straw Burn Detection layers
  private strawBurnLayer: TileLayer | null = null;
  private strawBurnData: any = null;

  // Planting Method Map layers (nationwide Philippines – Titiler COG)
  private plantingMethodDry2025Layer: TileLayer<XYZ> | null = null;
  private plantingMethodDry2024Layer: TileLayer<XYZ> | null = null;
  private riceGrowthStageLayer: TileLayer<XYZ> | null = null;
  private riceGrowthStageLoadingListener: ((loading: boolean) => void) | null = null;
  private irrigationTypeLayer: TileLayer<XYZ> | null = null;
  private irrigationTypeLoadingListener: ((loading: boolean) => void) | null = null;

  // CADI agricultural drought forecast (monthly Titiler COGs)
  private cadiDroughtLayer: TileLayer<XYZ> | null = null;
  private cadiDroughtFrameIndex: number = 0;

  // Rice pest risk maps (quarterly Titiler COGs)
  private pestRiskLayer: TileLayer<XYZ> | null = null;
  private pestRiskPestKey: PestRiskPestKey = 'bacterial_leaf_blight';
  private pestRiskQuarterIndex: number = 0;

  // Isabela PhilRice: NDVI + ortho = Titiler WebMercatorQuad XYZ
  private isabelaPhilriceNDVILayer: TileLayer<XYZ> | null = null;
  private isabelaPhilriceOrthoLayer: TileLayer<XYZ> | null = null;
  // Cavite Silang: NDVI + ortho = Titiler WebMercatorQuad XYZ
  private caviteSilangNDVILayer: TileLayer<XYZ> | null = null;
  private caviteSilangOrthoLayer: TileLayer<XYZ> | null = null;
  /** Registry-driven drone sites (`droneSites.ts`): at most one layer at a time, keyed by layer id. */
  private droneSiteLayers: Record<string, TileLayer<XYZ>> = {};
  private droneSiteListener: ((activeLayerId: string | null) => void) | null = null;
  /** Computed orthophoto index map (NDVI/NDRE/ExG/SAVI expression tiles). */
  private droneOrthoIndexLayer: TileLayer<XYZ> | null = null;

  // Change detection layers
  private changeDetectionBeforeLayer: TileLayer | null = null;
  private changeDetectionAfterLayer: TileLayer | null = null;
  private swipePosition: number = 50; // Percentage from left (0-100)
  private changeDetectionActive = false;
  private beforePreRenderKey: any = null;
  private beforePostRenderKey: any = null;
  private afterPreRenderKey: any = null;
  private afterPostRenderKey: any = null;

  // Layer configurations
  private readonly wmsConfigs = {
    yield: {
      url: DIGISAKA_API_ORIGIN + '/geoserver/NDVI_Yield/wms',
      layers: 'NDVI_Yield:database_for_webgis',
      styles: 'ndvi',
      defaultTime: '2024-01-01',
      visible: false
    }
  } as const;

  constructor(map: Map) {
    this.map = map;
    this.initializeWMSLayers();
  }

  // Create base layers
  static createBaseLayers() {
    const satelliteLayer = new TileLayer({
      source: new XYZ({
        url: 'https://api.maptiler.com/maps/satellite-v4/{z}/{x}/{y}@2x.jpg?key=Otbh9YhFMbwux7HyoffB',
        crossOrigin: 'anonymous',
      }),
    });
    satelliteLayer.set('layerType', 'baseLayer');
    satelliteLayer.set('baseLayerName', 'satellite');

    const hybridLayer = new TileLayer({
      source: new XYZ({
        url: 'https://api.maptiler.com/maps/hybrid-v4/{z}/{x}/{y}@2x.jpg?key=Otbh9YhFMbwux7HyoffB',
        crossOrigin: 'anonymous',
      }),
    });
    hybridLayer.set('layerType', 'baseLayer');
    hybridLayer.set('baseLayerName', 'hybrid');

    const terrainLayer = new TileLayer({
      source: new XYZ({
        url: 'https://api.maptiler.com/maps/topo-v4/{z}/{x}/{y}@2x.png?key=Otbh9YhFMbwux7HyoffB',
        crossOrigin: 'anonymous',
      }),
    });
    terrainLayer.set('layerType', 'baseLayer');
    terrainLayer.set('baseLayerName', 'terrain');

    const googleSatelliteLayer = new TileLayer({
      source: new XYZ({
        url: 'https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
        crossOrigin: 'anonymous',
      }),
    });
    googleSatelliteLayer.set('layerType', 'baseLayer');
    googleSatelliteLayer.set('baseLayerName', 'googleSatellite');

    const googleHybridLayer = new TileLayer({
      source: new XYZ({
        url: 'https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}',
        crossOrigin: 'anonymous',
      }),
    });
    googleHybridLayer.set('layerType', 'baseLayer');
    googleHybridLayer.set('baseLayerName', 'googleHybrid');

    const googleTerrainLayer = new TileLayer({
      source: new XYZ({
        url: 'https://mt1.google.com/vt/lyrs=p&x={x}&y={y}&z={z}',
        crossOrigin: 'anonymous',
      }),
    });
    googleTerrainLayer.set('layerType', 'baseLayer');
    googleTerrainLayer.set('baseLayerName', 'googleTerrain');

    return {
      satellite: satelliteLayer,
      hybrid: hybridLayer,
      terrain: terrainLayer,
      googleSatellite: googleSatelliteLayer,
      googleHybrid: googleHybridLayer,
      googleTerrain: googleTerrainLayer
    };
  }

  // Initialize WMS layers
  private initializeWMSLayers() {
    // Yield Layer - Simplified configuration
    this.yieldLayer = new ImageLayer({
      source: new ImageWMS({
        url: this.wmsConfigs.yield.url,
        params: {
          'LAYERS': this.wmsConfigs.yield.layers,
          'FORMAT': 'image/png',
          'TRANSPARENT': true,
          'TIME': this.wmsConfigs.yield.defaultTime,
          'STYLES': this.wmsConfigs.yield.styles || '',
          'VERSION': '1.1.1',
          'SRS': 'EPSG:3857'
        },
        serverType: 'geoserver',
        crossOrigin: 'anonymous'
      }),
      visible: this.wmsConfigs.yield.visible,
      opacity: 1,
    });

    this.yieldLayer.setZIndex(100);
    this.map.addLayer(this.yieldLayer);
  }

  toggleCoconutCropLayer(visible: boolean, opacity: number = 1): [number, number, number, number] | null {
    if (this.coconutLayer) {
      this.map.removeLayer(this.coconutLayer);
      this.coconutLayer = null;
    }
    if (!visible) {
      this.map.render();
      return null;
    }

    const extent = transformExtent(COCONUT_CROP_BBOX, 'EPSG:4326', 'EPSG:3857');
    this.coconutLayer = new TileLayer({
      source: new XYZ({
        url: buildCoconutCropTileUrl(),
        crossOrigin: 'anonymous',
        tileSize: 512,
        interpolate: false,
        extent,
      }),
      visible: true,
      opacity,
      zIndex: 200,
    });
    this.map.addLayer(this.coconutLayer);
    this.map.render();
    return COCONUT_CROP_BBOX;
  }

  toggleMaizeCropLayer(visible: boolean, opacity: number = 1): [number, number, number, number] | null {
    if (this.maizeLayer) {
      this.map.removeLayer(this.maizeLayer);
      this.maizeLayer = null;
    }
    if (!visible) {
      this.map.render();
      return null;
    }

    const extent = transformExtent(MAIZE_CROP_BBOX, 'EPSG:4326', 'EPSG:3857');
    this.maizeLayer = new TileLayer({
      source: new XYZ({
        url: buildMaizeCropTileUrl(),
        crossOrigin: 'anonymous',
        tileSize: 512,
        cacheSize: 4096,
        interpolate: false,
        extent,
      }),
      visible: true,
      opacity,
      zIndex: 200,
    });
    this.map.addLayer(this.maizeLayer);
    this.map.render();
    return MAIZE_CROP_BBOX;
  }

  toggleRiceCropLayer(visible: boolean, opacity: number = 1): [number, number, number, number] | null {
    if (this.riceLayer) {
      this.map.removeLayer(this.riceLayer);
      this.riceLayer = null;
    }
    if (!visible) {
      this.map.render();
      return null;
    }

    const extent = transformExtent(RICE_CROP_BBOX, 'EPSG:4326', 'EPSG:3857');
    this.riceLayer = new TileLayer({
      source: new XYZ({
        url: buildRiceCropTileUrl(),
        crossOrigin: 'anonymous',
        tileSize: 512,
        cacheSize: 4096,
        interpolate: false,
        extent,
      }),
      visible: true,
      opacity,
      zIndex: 200,
    });
    this.map.addLayer(this.riceLayer);
    this.map.render();
    return RICE_CROP_BBOX;
  }

  toggleSugarcaneCropLayer(visible: boolean, opacity: number = 1): [number, number, number, number] | null {
    if (this.sugarCaneLayer) {
      this.map.removeLayer(this.sugarCaneLayer);
      this.sugarCaneLayer = null;
    }
    if (!visible) {
      this.map.render();
      return null;
    }

    const extent = transformExtent(SUGARCANE_CROP_BBOX, 'EPSG:4326', 'EPSG:3857');
    this.sugarCaneLayer = new TileLayer({
      source: new XYZ({
        url: buildSugarcaneCropTileUrl(),
        crossOrigin: 'anonymous',
        tileSize: 512,
        cacheSize: 4096,
        interpolate: false,
        extent,
      }),
      visible: true,
      opacity,
      zIndex: 200,
    });
    this.map.addLayer(this.sugarCaneLayer);
    this.map.render();
    return SUGARCANE_CROP_BBOX;
  }

  // NEW: Toggle crop area layers
  async toggleCropAreaLayer(cropType: CropAreaLayerKey, visible: boolean, onLoadingStart?: () => string, onLoadingEnd?: (toastId: string, success: boolean, message: string) => void): Promise<void> {
    if (cropType === 'coconut') {
      this.toggleCoconutCropLayer(visible);
      if (visible && onLoadingEnd) {
        onLoadingEnd('', true, 'Loaded Coconut Area Layer Successfully!');
      }
      return;
    }

    if (cropType === 'maize') {
      this.toggleMaizeCropLayer(visible);
      if (visible && onLoadingEnd) {
        onLoadingEnd('', true, 'Loaded Maize Area Layer Successfully!');
      }
      return;
    }

    if (cropType === 'rice') {
      this.toggleRiceCropLayer(visible);
      if (visible && onLoadingEnd) {
        onLoadingEnd('', true, 'Loaded Rice Area Layer Successfully!');
      }
      return;
    }

    if (cropType === 'sugarCane') {
      this.toggleSugarcaneCropLayer(visible);
      if (visible && onLoadingEnd) {
        onLoadingEnd('', true, 'Loaded Sugarcane Area Layer Successfully!');
      }
      return;
    }
  }

  // NEW: Get crop area layer by type
  private getCropAreaLayer(cropType: CropAreaLayerKey): TileLayer | ImageLayer | null {
    switch (cropType) {
      case 'maize':
        return this.maizeLayer;
      case 'sugarCane':
        return this.sugarCaneLayer;
      case 'rice':
        return this.riceLayer;
      case 'coconut':
        return this.coconutLayer;
      default:
        return null;
    }
  }

  // NEW: Set crop area layer opacity
  setCropAreaLayerOpacity(cropType: CropAreaLayerKey, opacity: number): void {
    const layer = this.getCropAreaLayer(cropType);
    if (layer) {
      layer.setOpacity(opacity);
      this.map.render();
    }
  }

  // NEW: Check if crop area layer is visible
  isCropAreaLayerVisible(cropType: CropAreaLayerKey): boolean {
    const layer = this.getCropAreaLayer(cropType);
    return layer?.getVisible() ?? false;
  }

  // NEW: Hide all crop area layers
  hideAllCropAreaLayers(): void {
    (['maize', 'sugarCane', 'rice', 'coconut'] as const).forEach(cropType => {
      const layer = this.getCropAreaLayer(cropType);
      if (layer && layer.getVisible()) {
        layer.setVisible(false);
      }
    });
    this.map.render();
  }

  // Update rice forecast layer
  updateRiceForecastLayer(forecastData: any, opacity: number = 1) {
    // Remove existing forecast layer
    if (this.riceForecastLayer) {
      this.map.removeLayer(this.riceForecastLayer);
      this.riceForecastLayer = null;
    }

    // If no forecast data, just return
    if (!forecastData || !forecastData.forecasts) {
      this.riceForecastData = null;
      return;
    }

    // Store forecast data for legend
    this.riceForecastData = forecastData;

    // Get the tile URL from the first forecast month
    const forecastMonth = Object.values(forecastData.forecasts)[0] as any;
    if (!forecastMonth || !forecastMonth.tile_url) {
      return;
    }

    const tileUrl = forecastMonth.tile_url;

    // Create new forecast tile layer
    this.riceForecastLayer = new TileLayer({
      source: new XYZ({
        url: tileUrl,
        crossOrigin: 'anonymous',
      }),
      opacity: opacity,
      zIndex: 50, // Above base layer but below other analysis layers
    });

    this.map.addLayer(this.riceForecastLayer);
    this.map.render();
  }

  // Toggle rice forecast layer visibility
  toggleRiceForecastLayer(visible: boolean) {
    if (this.riceForecastLayer) {
      this.riceForecastLayer.setVisible(visible);
      this.map.render();
    }
  }

  // Set rice forecast layer opacity
  setRiceForecastLayerOpacity(opacity: number) {
    if (this.riceForecastLayer) {
      this.riceForecastLayer.setOpacity(opacity);
      this.map.render();
    }
  }

  // Update tree detection layer
  updateTreeDetectionLayer(treeDetectionData: any, opacity: number = 1) {

    // Remove existing tree detection layer
    if (this.treeDetectionLayer) {
      this.map.removeLayer(this.treeDetectionLayer);
      this.treeDetectionLayer = null;
    }

    // If no tree detection data, just return
    if (!treeDetectionData || !treeDetectionData.visualization_image) {
      this.treeDetectionData = null;
      return;
    }

    // Store tree detection data
    this.treeDetectionData = treeDetectionData;

    try {
      // Convert base64 image to data URL
      const imageFormat = treeDetectionData.visualization_image_format || 'png';
      const imageData = treeDetectionData.visualization_image;
      const dataUrl = `data:image/${imageFormat};base64,${imageData}`;

      // Create image element
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        // Get current map view extent or use default
        const view = this.map.getView();
        const extent = view.calculateExtent(this.map.getSize());
        
        // Create static image source with current map extent
        const imageSource = new ImageStatic({
          url: dataUrl,
          imageExtent: extent,
        });

        // Create image layer
        this.treeDetectionLayer = new ImageLayer({
          source: imageSource,
          opacity: opacity,
          zIndex: 60, // Above rice forecast but below other analysis layers
        });

        this.map.addLayer(this.treeDetectionLayer);
        this.map.render();
      };

      img.onerror = (error) => {
        console.error('MapLayerManager: Error loading tree detection image:', error);
      };

      img.src = dataUrl;
    } catch (error) {
      console.error('MapLayerManager: Error creating tree detection layer:', error);
    }
  }

  // Toggle tree detection layer visibility
  toggleTreeDetectionLayer(visible: boolean) {
    if (this.treeDetectionLayer) {
      this.treeDetectionLayer.setVisible(visible);
      this.map.render();
    }
  }

  // Set tree detection layer opacity
  setTreeDetectionLayerOpacity(opacity: number) {
    if (this.treeDetectionLayer) {
      this.treeDetectionLayer.setOpacity(opacity);
      this.map.render();
    }
  }

  // Update tree detection points layer (GeoJSON points with small pin style)
  updateTreeDetectionPointsLayer(geojson: any) {
    if (this.treeDetectionPointsLayer) {
      this.map.removeLayer(this.treeDetectionPointsLayer);
      this.treeDetectionPointsLayer = null;
    }
    if (!geojson || geojson.type !== 'FeatureCollection') {
      this.map.render();
      return;
    }
    const features = geojson.features;
    if (!Array.isArray(features) || features.length === 0) {
      this.map.render();
      return;
    }
    try {
      const format = new GeoJSON({
        featureProjection: 'EPSG:3857',
      });
      const vectorSource = new VectorSource({
        features: format.readFeatures(geojson),
      });
      const pointStyle = new Style({
        image: new Circle({
          radius: 4,
          fill: new Fill({ color: '#22c55e' }),
          stroke: new Stroke({ color: '#15803d', width: 1.5 }),
        }),
      });
      this.treeDetectionPointsLayer = new VectorLayer({
        source: vectorSource,
        style: pointStyle,
        zIndex: 65,
      });
      this.map.addLayer(this.treeDetectionPointsLayer);
      this.map.render();
    } catch (error) {
      console.error('MapLayerManager: Error adding tree detection points layer:', error);
      this.map.render();
    }
  }

  private notifyCropfieldVertexHistory() {
    this.cropfieldVertexHistoryListener?.({
      canUndo: this.cropfieldVertexUndoStack.length > 0,
      canRedo: this.cropfieldVertexRedoStack.length > 0,
    });
  }

  private resetCropfieldVertexHistory() {
    this.cropfieldVertexUndoStack = [];
    this.cropfieldVertexRedoStack = [];
    this.cropfieldVertexPendingSnapshot = null;
    this.notifyCropfieldVertexHistory();
  }

  private cloneEditGeometry(): Geometry | null {
    const feature = this.cropfieldVertexEditSource?.getFeatures()?.[0];
    const geom = feature?.getGeometry();
    return geom ? geom.clone() : null;
  }

  private applyEditGeometry(geom: Geometry) {
    const feature = this.cropfieldVertexEditSource?.getFeatures()?.[0];
    if (!feature) return false;
    feature.setGeometry(geom.clone());
    this.cropfieldVertexEditLayer?.changed();
    this.map.render();
    return true;
  }

  private onCropfieldVertexModifyStart = () => {
    this.cropfieldVertexPendingSnapshot = this.cloneEditGeometry();
  };

  private onCropfieldVertexModifyEnd = () => {
    const before = this.cropfieldVertexPendingSnapshot;
    this.cropfieldVertexPendingSnapshot = null;
    if (!before) return;
    const after = this.cloneEditGeometry();
    if (!after) return;
    if (JSON.stringify(before.getCoordinates()) === JSON.stringify(after.getCoordinates())) {
      return;
    }
    this.cropfieldVertexUndoStack.push(before);
    if (this.cropfieldVertexUndoStack.length > this.cropfieldVertexMaxHistory) {
      this.cropfieldVertexUndoStack.shift();
    }
    this.cropfieldVertexRedoStack = [];
    this.notifyCropfieldVertexHistory();
  };

  setCropfieldVertexHistoryListener(
    listener: ((state: { canUndo: boolean; canRedo: boolean }) => void) | null
  ) {
    this.cropfieldVertexHistoryListener = listener;
    this.notifyCropfieldVertexHistory();
  }

  undoCropfieldVertexEdit(): boolean {
    if (this.cropfieldVertexUndoStack.length === 0) return false;
    const current = this.cloneEditGeometry();
    const previous = this.cropfieldVertexUndoStack.pop();
    if (!previous || !this.applyEditGeometry(previous)) return false;
    if (current) this.cropfieldVertexRedoStack.push(current);
    this.notifyCropfieldVertexHistory();
    return true;
  }

  redoCropfieldVertexEdit(): boolean {
    if (this.cropfieldVertexRedoStack.length === 0) return false;
    const current = this.cloneEditGeometry();
    const next = this.cropfieldVertexRedoStack.pop();
    if (!next || !this.applyEditGeometry(next)) return false;
    if (current) this.cropfieldVertexUndoStack.push(current);
    this.notifyCropfieldVertexHistory();
    return true;
  }

  canUndoCropfieldVertexEdit(): boolean {
    return this.cropfieldVertexUndoStack.length > 0;
  }

  canRedoCropfieldVertexEdit(): boolean {
    return this.cropfieldVertexRedoStack.length > 0;
  }

  private clearCropfieldVertexEditOnly(restoreDelineationVisibility = true) {
    if (this.cropfieldVertexModify) {
      this.cropfieldVertexModify.un('modifystart', this.onCropfieldVertexModifyStart);
      this.cropfieldVertexModify.un('modifyend', this.onCropfieldVertexModifyEnd);
      this.map.removeInteraction(this.cropfieldVertexModify);
      this.cropfieldVertexModify = null;
    }
    if (this.cropfieldVertexSnap) {
      this.map.removeInteraction(this.cropfieldVertexSnap);
      this.cropfieldVertexSnap = null;
    }
    if (this.cropfieldVertexNeighborSnap) {
      this.map.removeInteraction(this.cropfieldVertexNeighborSnap);
      this.cropfieldVertexNeighborSnap = null;
    }
    if (this.cropfieldVertexEditLayer) {
      this.map.removeLayer(this.cropfieldVertexEditLayer);
      this.cropfieldVertexEditLayer = null;
    }
    this.cropfieldVertexEditSource = null;
    this.resetCropfieldVertexHistory();
    if (this.cropfieldDelineationLayer) {
      this.cropfieldDelineationLayer.setVisible(true);
      this.cropfieldDelineationLayer.getSource().getFeatures().forEach((f) => {
        f.unset('delineation_hidden_for_edit');
      });
      this.cropfieldDelineationLayer.changed();
    }
  }

  /** Outer ring coordinates as vertex list (drops closing duplicate point). */
  private static ringToVertices(ring: Coordinate[]): Coordinate[] {
    if (ring.length < 2) return [...ring];
    const first = ring[0];
    const last = ring[ring.length - 1];
    if (first[0] === last[0] && first[1] === last[1]) {
      return ring.slice(0, -1);
    }
    return [...ring];
  }

  /** MultiPoint at every polygon vertex for visible edit dots (updates as geometry changes). */
  private createCropfieldVertexDotsGeometry(feature: Feature): MultiPoint | undefined {
    const g = feature.getGeometry();
    if (!g) return undefined;
    const t = g.getType();
    const all: Coordinate[] = [];
    if (t === 'Polygon') {
      const ring = (g as Polygon).getCoordinates()[0];
      if (ring) all.push(...MapLayerManager.ringToVertices(ring));
    } else if (t === 'MultiPolygon') {
      for (const poly of (g as MultiPolygon).getPolygons()) {
        const ring = poly.getCoordinates()[0];
        if (ring) all.push(...MapLayerManager.ringToVertices(ring));
      }
    } else {
      return undefined;
    }
    if (all.length === 0) return undefined;
    return new MultiPoint(all);
  }

  /** Highlight one delineated polygon (field-level AI tool). Pass null to clear selection. */
  setCropfieldDelineationSelectedIndex(index: number | null) {
    if (!this.cropfieldDelineationLayer) return;
    const source = this.cropfieldDelineationLayer.getSource();
    source.getFeatures().forEach((f) => {
      if (index === null) {
        f.set('delineation_selected', false);
      } else {
        f.set('delineation_selected', f.get('delineation_index') === index);
      }
    });
    this.cropfieldDelineationLayer.changed();
    this.map.render();
  }

  /** Multi-select: mark checked blocks (sidebar checkboxes / Ctrl+click on map). */
  setCropfieldDelineationCheckedIndexes(indexes: number[]) {
    if (!this.cropfieldDelineationLayer) return;
    const checked = new Set(indexes);
    const source = this.cropfieldDelineationLayer.getSource();
    source.getFeatures().forEach((f) => {
      const idx = f.get('delineation_index');
      f.set(
        'delineation_checked',
        typeof idx === 'number' && checked.has(idx)
      );
    });
    this.cropfieldDelineationLayer.changed();
    this.map.render();
  }

  /** When true, map highlights use checkboxes only (not last-click focus). */
  setCropfieldDelineationMultiSelectMode(active: boolean) {
    if (!this.cropfieldDelineationLayer) return;
    const source = this.cropfieldDelineationLayer.getSource();
    source.getFeatures().forEach((f) => {
      f.set('delineation_multi_select', active);
      if (active) f.set('delineation_selected', false);
    });
    this.cropfieldDelineationLayer.changed();
    this.map.render();
  }

  /**
   * Enter vertex-edit mode for one polygon. Other detected fields stay on the map
   * so the outline can be aligned without overlapping them.
   */
  startCropfieldVertexEdit(featureIndex: number): boolean {
    if (!this.cropfieldDelineationLayer) return false;
    const source = this.cropfieldDelineationLayer.getSource();
    const feat = source.getFeatures().find((f) => f.get('delineation_index') === featureIndex);
    if (!feat) return false;
    this.clearCropfieldVertexEditOnly(true);
    feat.set('delineation_hidden_for_edit', true);
    this.cropfieldDelineationLayer.changed();
    const clone = feat.clone() as Feature;
    clone.set('delineation_index', featureIndex);
    // Match manual draw field: blue fill/stroke + blue vertex dots; Modify handles match draw mode (coral)
    const editPolygonStyle = new Style({
      fill: new Fill({ color: 'rgba(59, 130, 246, 0.3)' }),
      stroke: new Stroke({ color: '#3B82F6', width: 3 }),
    });
    const vertexDotStyle = new Style({
      image: new Circle({
        radius: 6,
        fill: new Fill({ color: '#3B82F6' }),
        stroke: new Stroke({ color: '#ffffff', width: 2 }),
      }),
      geometry: (feat) => this.createCropfieldVertexDotsGeometry(feat as Feature) ?? undefined,
    });
    clone.setStyle([editPolygonStyle, vertexDotStyle]);
    this.cropfieldVertexEditSource = new VectorSource({ features: [clone] });
    this.cropfieldVertexEditLayer = new VectorLayer({
      source: this.cropfieldVertexEditSource,
      zIndex: 67,
    });
    this.map.addLayer(this.cropfieldVertexEditLayer);
    this.cropfieldDelineationLayer.setVisible(true);
    const modifyVertexStyle = new Style({
      image: new Circle({
        radius: 8,
        fill: new Fill({ color: '#FF6B6B' }),
        stroke: new Stroke({ color: '#ffffff', width: 2 }),
      }),
    });
    this.cropfieldVertexModify = new Modify({
      source: this.cropfieldVertexEditSource,
      style: modifyVertexStyle,
    });
    this.cropfieldVertexModify.on('modifystart', this.onCropfieldVertexModifyStart);
    this.cropfieldVertexModify.on('modifyend', this.onCropfieldVertexModifyEnd);
    this.cropfieldVertexSnap = new Snap({ source: this.cropfieldVertexEditSource });
    const neighborSource = new VectorSource({
      features: source
        .getFeatures()
        .filter((f) => f.get('delineation_index') !== featureIndex),
    });
    this.cropfieldVertexNeighborSnap = new Snap({ source: neighborSource });
    this.map.addInteraction(this.cropfieldVertexModify);
    this.map.addInteraction(this.cropfieldVertexSnap);
    this.map.addInteraction(this.cropfieldVertexNeighborSnap);
    this.resetCropfieldVertexHistory();
    this.map.render();
    return true;
  }

  getCropfieldVertexEditGeoJson(): Record<string, unknown> | null {
    if (!this.cropfieldVertexEditSource || this.cropfieldVertexEditSource.getFeatures().length === 0) {
      return null;
    }
    const feature = this.cropfieldVertexEditSource.getFeatures()[0];
    const format = new GeoJSON({
      featureProjection: 'EPSG:3857',
      dataProjection: 'EPSG:4326',
    });
    return format.writeFeatureObject(feature) as Record<string, unknown>;
  }

  /** Exit edit mode and show all delineated fields again. */
  cancelCropfieldVertexEdit() {
    this.clearCropfieldVertexEditOnly(true);
    this.map.render();
  }

  /** Export one delineated polygon in EPSG:4326 (without removing it from the layer). */
  getCropfieldDelineationFeatureGeoJson(featureIndex: number): Record<string, unknown> | null {
    if (!this.cropfieldDelineationLayer) return null;
    const source = this.cropfieldDelineationLayer.getSource();
    const feat = source
      .getFeatures()
      .find((f) => Number(f.get('delineation_index')) === featureIndex);
    if (!feat) return null;
    const format = new GeoJSON({
      featureProjection: 'EPSG:3857',
      dataProjection: 'EPSG:4326',
    });
    return format.writeFeatureObject(feat) as Record<string, unknown>;
  }

  getCropfieldDelineationCount(): number {
    return this.lastCropfieldGeoJson?.features?.length ?? 0;
  }

  getCropfieldDelineationLayer(): VectorLayer<VectorSource> | null {
    return this.cropfieldDelineationLayer;
  }

  /** Remove one delineated polygon and re-render the remaining fields (re-indexed). */
  private removeCropfieldDelineationFeature(featureIndex: number): number {
    if (!this.lastCropfieldGeoJson?.features) return 0;
    const features = this.lastCropfieldGeoJson.features.filter(
      (_: unknown, i: number) => i !== featureIndex
    );
    if (features.length === 0) {
      this.updateCropfieldDelineationLayer(null, null);
      return 0;
    }
    const updatedGeoJson = { ...this.lastCropfieldGeoJson, features };
    this.updateCropfieldDelineationLayer(updatedGeoJson, this.lastCropfieldCrs, false);
    return features.length;
  }

  /** Export the edited polygon in EPSG:4326 and keep remaining delineations on the map. */
  confirmCropfieldVertexEdit(): { geojson: Record<string, unknown>; remainingCount: number } | null {
    const editSource = this.cropfieldVertexEditSource;
    if (!editSource || editSource.getFeatures().length === 0) return null;
    const featureIndex = editSource.getFeatures()[0].get('delineation_index') as number;
    const gj = this.getCropfieldVertexEditGeoJson();
    if (!gj || typeof featureIndex !== 'number') return null;
    this.clearCropfieldVertexEditOnly(false);
    const remainingCount = this.removeCropfieldDelineationFeature(featureIndex);
    this.map.render();
    return { geojson: gj, remainingCount };
  }

  /**
   * Apply vertex edits back onto the same detected field (map projection).
   * Do not rebuild the whole layer from GeoJSON — that can drop or misplace the outline.
   */
  applyCropfieldVertexEditInPlace(): {
    featureIndex: number;
    geojson: Record<string, unknown>;
    featureCount: number;
  } | null {
    const editSource = this.cropfieldVertexEditSource;
    if (!editSource || editSource.getFeatures().length === 0) return null;
    const editFeat = editSource.getFeatures()[0];
    const featureIndex = editFeat.get('delineation_index') as number;
    const editedGeom = editFeat.getGeometry();
    if (typeof featureIndex !== 'number' || !editedGeom) return null;

    const gj = this.getCropfieldVertexEditGeoJson();
    const delineationSource = this.cropfieldDelineationLayer?.getSource();
    const live = delineationSource
      ?.getFeatures()
      .find((f) => Number(f.get('delineation_index')) === featureIndex);
    if (!live) return null;

    live.setGeometry(editedGeom.clone());
    live.unset('delineation_hidden_for_edit');

    if (gj && Array.isArray(this.lastCropfieldGeoJson?.features)) {
      const prev = this.lastCropfieldGeoJson.features[featureIndex] || {};
      this.lastCropfieldGeoJson = {
        ...this.lastCropfieldGeoJson,
        features: this.lastCropfieldGeoJson.features.map((row, i) =>
          i === featureIndex
            ? {
                ...(typeof row === 'object' && row ? row : {}),
                type: 'Feature',
                geometry: (gj as { geometry?: unknown }).geometry,
                properties: {
                  ...(typeof prev === 'object' && prev && 'properties' in prev
                    ? (prev as { properties?: Record<string, unknown> }).properties || {}
                    : {}),
                  ...(typeof gj.properties === 'object' && gj.properties
                    ? (gj.properties as Record<string, unknown>)
                    : {}),
                },
              }
            : row
        ),
      };
    }

    this.clearCropfieldVertexEditOnly(false);
    this.setCropfieldDelineationSelectedIndex(featureIndex);
    this.cropfieldDelineationLayer?.changed();
    this.map.render();
    return {
      featureIndex,
      geojson: gj ?? { type: 'Feature', geometry: null },
      featureCount: delineationSource.getFeatures().length,
    };
  }

  /** Export all current delineated polygons as a FeatureCollection (EPSG:4326). */
  getCropfieldDelineationFeatureCollectionGeoJson(): {
    type: 'FeatureCollection';
    features: Record<string, unknown>[];
  } | null {
    if (!this.cropfieldDelineationLayer) {
      const cached = this.lastCropfieldGeoJson;
      if (cached?.type === 'FeatureCollection' && Array.isArray(cached.features)) {
        return JSON.parse(JSON.stringify(cached));
      }
      return null;
    }
    const source = this.cropfieldDelineationLayer.getSource();
    const feats = source
      .getFeatures()
      .slice()
      .sort(
        (a, b) =>
          (Number(a.get('delineation_index')) || 0) - (Number(b.get('delineation_index')) || 0)
      );
    if (feats.length === 0) return null;
    const format = new GeoJSON({
      featureProjection: 'EPSG:3857',
      dataProjection: 'EPSG:4326',
    });
    const features = feats.map((f) => format.writeFeatureObject(f) as Record<string, unknown>);
    return { type: 'FeatureCollection', features };
  }

  /** Remove one delineated polygon and return the new count (public for Split review). */
  removeCropfieldDelineationFeatureAt(featureIndex: number): number {
    const remaining = this.removeCropfieldDelineationFeature(featureIndex);
    this.map.render();
    return remaining;
  }

  /** Remove several delineated polygons in one pass (re-index once). */
  removeCropfieldDelineationFeaturesAt(featureIndices: number[]): number {
    if (!this.lastCropfieldGeoJson?.features?.length) return 0;
    const take = new Set(
      featureIndices.map((i) => Math.trunc(Number(i))).filter((i) => Number.isFinite(i) && i >= 0)
    );
    if (take.size === 0) return this.lastCropfieldGeoJson.features.length;
    const features = this.lastCropfieldGeoJson.features.filter(
      (_: unknown, i: number) => !take.has(i)
    );
    if (features.length === 0) {
      this.updateCropfieldDelineationLayer(null, null);
      return 0;
    }
    this.updateCropfieldDelineationLayer(
      { ...this.lastCropfieldGeoJson, type: 'FeatureCollection', features },
      this.lastCropfieldCrs,
      false
    );
    this.map.render();
    return features.length;
  }

  /** Export one delineated polygon (no vertex edit) and keep remaining delineations on the map. */
  confirmCropfieldDelineationFeature(
    featureIndex: number
  ): { geojson: Record<string, unknown>; remainingCount: number } | null {
    const gj = this.getCropfieldDelineationFeatureGeoJson(featureIndex);
    if (!gj) return null;
    const remainingCount = this.removeCropfieldDelineationFeature(featureIndex);
    this.map.render();
    return { geojson: gj, remainingCount };
  }

  /**
   * Export several delineated polygons as one Feature (MultiPolygon when 2+),
   * then remove those from the delineation layer.
   * ``featureIndices`` are ``delineation_index`` values (stable ids), not list order.
   */
  confirmCropfieldDelineationFeatures(
    featureIndices: number[]
  ): { geojson: Record<string, unknown>; remainingCount: number } | null {
    const unique = [
      ...new Set(
        featureIndices
          .map((i) => Math.trunc(Number(i)))
          .filter((i) => Number.isFinite(i) && i >= 0)
      ),
    ].sort((a, b) => a - b);
    if (unique.length === 0) return null;

    const take = new Set(unique);
    const polygonCoords: number[][][][] = [];

    // Prefer live OL features keyed by delineation_index (avoids FC array-index drift).
    if (this.cropfieldDelineationLayer) {
      const source = this.cropfieldDelineationLayer.getSource();
      const feats = source?.getFeatures() || [];
      for (const idx of unique) {
        const feat = feats.find((f) => Number(f.get('delineation_index')) === idx);
        if (!feat) continue;
        const gj = this.getCropfieldDelineationFeatureGeoJson(idx);
        const geom = (gj as { geometry?: { type?: string; coordinates?: unknown } } | null)?.geometry;
        if (!geom?.coordinates) continue;
        if (geom.type === 'Polygon' && Array.isArray(geom.coordinates)) {
          polygonCoords.push(geom.coordinates as number[][][]);
        } else if (geom.type === 'MultiPolygon' && Array.isArray(geom.coordinates)) {
          for (const poly of geom.coordinates as number[][][][]) {
            if (Array.isArray(poly)) polygonCoords.push(poly);
          }
        }
      }
    } else {
      for (const idx of unique) {
        const row = this.lastCropfieldGeoJson?.features?.[idx];
        const geom = (row as { geometry?: { type?: string; coordinates?: unknown } } | undefined)?.geometry;
        if (!geom?.coordinates) continue;
        if (geom.type === 'Polygon' && Array.isArray(geom.coordinates)) {
          polygonCoords.push(geom.coordinates as number[][][]);
        } else if (geom.type === 'MultiPolygon' && Array.isArray(geom.coordinates)) {
          for (const poly of geom.coordinates as number[][][][]) {
            if (Array.isArray(poly)) polygonCoords.push(poly);
          }
        }
      }
    }

    if (polygonCoords.length === 0) return null;

    // Keep remaining by delineation_index (live OL source), then re-index 0..n-1
    let remainingFeatures: unknown[] = [];
    if (this.cropfieldDelineationLayer) {
      const source = this.cropfieldDelineationLayer.getSource();
      const format = new GeoJSON({
        featureProjection: 'EPSG:3857',
        dataProjection: 'EPSG:4326',
      });
      remainingFeatures = source
        .getFeatures()
        .filter((f) => !take.has(Number(f.get('delineation_index'))))
        .map((f) => format.writeFeatureObject(f));
    } else {
      const prevFeatures = Array.isArray(this.lastCropfieldGeoJson?.features)
        ? (this.lastCropfieldGeoJson!.features as unknown[])
        : [];
      remainingFeatures = prevFeatures.filter((_, i) => !take.has(i));
    }
    let remainingCount = 0;
    if (remainingFeatures.length === 0) {
      this.updateCropfieldDelineationLayer(null, null);
      remainingCount = 0;
    } else {
      this.updateCropfieldDelineationLayer(
        {
          ...(this.lastCropfieldGeoJson || {}),
          type: 'FeatureCollection',
          features: remainingFeatures,
        },
        this.lastCropfieldCrs,
        false
      );
      remainingCount = remainingFeatures.length;
    }
    this.map.render();

    const geojson: Record<string, unknown> = {
      type: 'Feature',
      properties: {
        block_count: polygonCoords.length,
        selected_indices: unique,
        source: 'ai_field_delineation',
      },
      geometry:
        polygonCoords.length === 1
          ? { type: 'Polygon', coordinates: polygonCoords[0] }
          : { type: 'MultiPolygon', coordinates: polygonCoords },
    };
    return { geojson, remainingCount };
  }

  isCropfieldVertexEditActive(): boolean {
    return this.cropfieldVertexModify !== null;
  }

  /**
   * Show crop-field polygons from Digisaka GPT `/cropfield-delineation/delineate/` (GeoJSON in raster CRS).
   */
  updateCropfieldDelineationLayer(geojson: any, crsString: string | null, fitExtent = true) {
    this.clearCropfieldVertexEditOnly(true);
    if (this.cropfieldDelineationLayer) {
      this.map.removeLayer(this.cropfieldDelineationLayer);
      this.cropfieldDelineationLayer = null;
    }
    this.lastCropfieldGeoJson = null;
    this.lastCropfieldCrs = null;
    if (!geojson || geojson.type !== 'FeatureCollection') {
      this.map.render();
      return;
    }
    const gjFeatures = geojson.features;
    if (!Array.isArray(gjFeatures) || gjFeatures.length === 0) {
      this.map.render();
      return;
    }

    this.lastCropfieldGeoJson = JSON.parse(JSON.stringify(geojson));
    this.lastCropfieldCrs = crsString;

    ensureProjectionsRegistered(crsString);
    const normalized =
      crsString && crsString.trim()
        ? crsString.trim().toUpperCase().startsWith('EPSG:')
          ? crsString.trim().toUpperCase()
          : `EPSG:${crsString.trim()}`
        : 'EPSG:4326';

    let dataProj = getProjection(normalized);
    if (!dataProj) {
      console.warn(
        'MapLayerManager: unknown CRS for crop-field GeoJSON, falling back to EPSG:4326:',
        crsString
      );
      dataProj = getProjection('EPSG:4326');
    }
    if (!dataProj) {
      this.map.render();
      return;
    }

    try {
      const format = new GeoJSON({
        dataProjection: dataProj,
        featureProjection: 'EPSG:3857',
      });
      const olFeatures = format.readFeatures(geojson);
      olFeatures.forEach((f, i) => {
        f.set('delineation_index', i);
        f.set('delineation_selected', false);
        f.set('delineation_checked', false);
        f.set('delineation_multi_select', false);
        const named =
          f.get('name') ||
          f.get('NAME') ||
          f.get('Name') ||
          f.get('farm_name') ||
          f.get('field_name') ||
          f.get('NAM');
        f.set('delineation_label', shortPolygonLabel(named, i));
      });
      const vectorSource = new VectorSource({
        features: olFeatures,
      });

      const delineationLabelGeometry = (feat: Feature): Geometry | undefined => {
        const g = feat.getGeometry();
        if (!g) return undefined;
        const t = g.getType();
        if (t === 'Polygon') {
          return (g as Polygon).getInteriorPoint();
        }
        if (t === 'MultiPolygon') {
          const polys = (g as MultiPolygon).getPolygons();
          return polys.length > 0 ? polys[0].getInteriorPoint() : undefined;
        }
        return undefined;
      };

      const styleFn = (feature: Feature) => {
        if (feature.get('delineation_hidden_for_edit')) return [];
        const sel = feature.get('delineation_selected');
        const chk = feature.get('delineation_checked');
        const multi = feature.get('delineation_multi_select') === true;
        const highlighted = multi ? chk : sel || chk;
        const idx = feature.get('delineation_index');
        const named = feature.get('delineation_label');
        const label = shortPolygonLabel(
          named,
          typeof idx === 'number' ? idx : 0,
        );
        const fillStroke = new Style({
          fill: new Fill({
            color: highlighted ? 'rgba(59, 130, 246, 0.35)' : 'rgba(251, 191, 36, 0.2)',
          }),
          stroke: new Stroke({
            color: highlighted ? '#3B82F6' : '#d97706',
            width: highlighted ? 3 : 2,
          }),
        });
        const labelStyle = new Style({
          geometry: delineationLabelGeometry,
          text: new Text({
            text: label,
            font: `bold ${getMapLabelFontSizePx(13)}px "Poppins", system-ui, sans-serif`,
            fill: new Fill({ color: highlighted ? '#ffffff' : '#fffbeb' }),
            stroke: new Stroke({
              color: highlighted ? '#1e40af' : '#78350f',
              width: Math.max(2, Math.round(3 * getMapLabelFontSizePx(13) / 13)),
            }),
            overflow: true,
            textAlign: 'center',
          }),
        });
        return [fillStroke, labelStyle];
      };
      this.cropfieldDelineationLayer = new VectorLayer({
        source: vectorSource,
        style: styleFn,
        zIndex: 66,
      });
      this.map.addLayer(this.cropfieldDelineationLayer);
      if (fitExtent) {
        const extent = vectorSource.getExtent();
        if (extent.every((x) => Number.isFinite(x))) {
          this.map.getView().fit(extent, {
            padding: [50, 50, 50, 50],
            duration: 800,
            maxZoom: 19,
          });
        }
      }
      this.map.render();
    } catch (error) {
      console.error('MapLayerManager: Error adding crop-field delineation layer:', error);
      this.map.render();
    }
  }

  /**
   * Crown segmentation polygons from Digisaka GPT `/tree-detection/crown-segmentation/`
   * (same GeoJSON + CRS pattern as crop-field delineation).
   */
  updateCrownSegmentationLayer(geojson: any, crsString: string | null) {
    if (this.crownSegmentationLayer) {
      this.map.removeLayer(this.crownSegmentationLayer);
      this.crownSegmentationLayer = null;
    }
    if (!geojson || geojson.type !== 'FeatureCollection') {
      this.map.render();
      return;
    }
    const features = geojson.features;
    if (!Array.isArray(features) || features.length === 0) {
      this.map.render();
      return;
    }

    ensureProjectionsRegistered(crsString);
    const normalized =
      crsString && crsString.trim()
        ? crsString.trim().toUpperCase().startsWith('EPSG:')
          ? crsString.trim().toUpperCase()
          : `EPSG:${crsString.trim()}`
        : 'EPSG:4326';

    let dataProj = getProjection(normalized);
    if (!dataProj) {
      console.warn(
        'MapLayerManager: unknown CRS for crown segmentation GeoJSON, falling back to EPSG:4326:',
        crsString
      );
      dataProj = getProjection('EPSG:4326');
    }
    if (!dataProj) {
      this.map.render();
      return;
    }

    try {
      const format = new GeoJSON({
        dataProjection: dataProj,
        featureProjection: 'EPSG:3857',
      });
      const vectorSource = new VectorSource({
        features: format.readFeatures(geojson),
      });
      const polyStyle = new Style({
        fill: new Fill({ color: 'rgba(16, 185, 129, 0.22)' }),
        stroke: new Stroke({ color: '#10b981', width: 2 }),
      });
      this.crownSegmentationLayer = new VectorLayer({
        source: vectorSource,
        style: polyStyle,
        zIndex: 67,
      });
      this.map.addLayer(this.crownSegmentationLayer);
      const extent = vectorSource.getExtent();
      if (extent.every((x) => Number.isFinite(x))) {
        this.map.getView().fit(extent, {
          padding: [50, 50, 50, 50],
          duration: 800,
          maxZoom: 19,
        });
      }
      this.map.render();
    } catch (error) {
      console.error('MapLayerManager: Error adding crown segmentation layer:', error);
      this.map.render();
    }
  }

  // Get rice forecast legend info
  getRiceForecastLegendInfo(): { colors: string[]; labels: string[]; description: string; min: number; max: number; scope?: string } | null {
    if (!this.riceForecastData || !this.riceForecastData.forecasts) {
      return null;
    }

    // Get the first forecast month data
    const forecastMonth = Object.values(this.riceForecastData.forecasts)[0] as any;
    if (!forecastMonth || !forecastMonth.vis_params) {
      return null;
    }

    const visParams = forecastMonth.vis_params;
    const palette = visParams.palette || [];
    const min = visParams.min !== undefined ? visParams.min : 0;
    const max = visParams.max !== undefined ? visParams.max : 7.5;

    // Create color stops for the legend - palette contains CSS color names or hex codes
    const colors = palette.length > 0 ? palette : ['#d73027', '#fee08b', '#1a9850'];

    // Create labels based on min/max values distributed evenly across the palette
    const numStops = colors.length;
    const labels = [];
    for (let i = 0; i < numStops; i++) {
      const value = min + (i / (numStops - 1)) * (max - min);
      labels.push(`${value.toFixed(1)} t/ha`);
    }

    return {
      colors: colors,
      labels: labels,
      description: `Rice yield forecast: ${min.toFixed(1)} - ${max.toFixed(1)} tons/hectare`,
      min: min,
      max: max,
      // Pass through scope so UI can distinguish farm vs large-scale forecasts
      scope: this.riceForecastData.scope
    };
  }

  // Update Black Sigatoka Risk Index layer
  updateBSRILayer(bsriData: any, currentIndex: number, opacity: number = 1) {
    // Remove existing BSRI layer
    if (this.bsriLayer) {
      this.map.removeLayer(this.bsriLayer);
      this.bsriLayer = null;
    }

    // If no BSRI data, just return
    if (!bsriData || !bsriData.tiles || bsriData.tiles.length === 0) {
      this.bsriData = null;
      return;
    }

    // Store BSRI data for legend
    this.bsriData = bsriData;

    // Get the tile URL from the current index
    const currentTile = bsriData.tiles[currentIndex];
    if (!currentTile || !currentTile.tile_url) {
      return;
    }

    const tileUrl = currentTile.tile_url;

    // Create new BSRI tile layer
    this.bsriLayer = new TileLayer({
      source: new XYZ({
        url: tileUrl,
        crossOrigin: 'anonymous',
      }),
      opacity: opacity,
      zIndex: 55, // Above rice forecast but below other layers
    });

    this.map.addLayer(this.bsriLayer);
    this.map.render();
  }

  // Update LST Philippines (MODIS) layer
  updateLSTPHLayer(lstData: any, currentIndex: number, opacity: number = 1) {
    // Remove existing LST PH layer
    if (this.lstPHLayer) {
      this.map.removeLayer(this.lstPHLayer);
      this.lstPHLayer = null;
    }

    // If no data, just clear and return
    if (!lstData || !lstData.tiles || lstData.tiles.length === 0) {
      this.lstPHData = null;
      return;
    }

    // Store for legend / future updates
    this.lstPHData = lstData;

    const currentTile = lstData.tiles[currentIndex];
    if (!currentTile || !currentTile.tile_url) {
      return;
    }

    const tileUrl = currentTile.tile_url;

    this.lstPHLayer = new TileLayer({
      source: new XYZ({
        url: tileUrl,
        crossOrigin: 'anonymous',
      }),
      opacity,
      zIndex: 54, // below BSRI but above base WMS
    });

    this.map.addLayer(this.lstPHLayer);
    this.map.render();
  }

  // Clear LST Philippines layer and data
  clearLSTPHLayer() {
    if (this.lstPHLayer) {
      this.map.removeLayer(this.lstPHLayer);
      this.lstPHLayer = null;
      this.map.render();
    }
    this.lstPHData = null;
  }

  // Update VHI Philippines (agricultural drought) layer
  updateVHIPHLayer(vhiData: any, currentIndex: number, opacity: number = 1) {
    // Remove existing VHI PH layer
    if (this.vhiPHLayer) {
      this.map.removeLayer(this.vhiPHLayer);
      this.vhiPHLayer = null;
    }

    // If no data, just clear and return
    if (!vhiData || !vhiData.tiles || vhiData.tiles.length === 0) {
      this.vhiPHData = null;
      return;
    }

    // Store for legend / future updates
    this.vhiPHData = vhiData;

    const currentTile = vhiData.tiles[currentIndex];
    if (!currentTile || !currentTile.tile_url) {
      return;
    }

    const tileUrl = currentTile.tile_url;

    this.vhiPHLayer = new TileLayer({
      source: new XYZ({
        url: tileUrl,
        crossOrigin: 'anonymous',
      }),
      opacity,
      zIndex: 54, // similar to LST PH
    });

    this.map.addLayer(this.vhiPHLayer);
    this.map.render();
  }

  // Clear VHI Philippines layer and data
  clearVHIPHLayer() {
    if (this.vhiPHLayer) {
      this.map.removeLayer(this.vhiPHLayer);
      this.vhiPHLayer = null;
      this.map.render();
    }
    this.vhiPHData = null;
  }

  // Toggle BSRI layer visibility
  toggleBSRILayer(visible: boolean) {
    if (this.bsriLayer) {
      this.bsriLayer.setVisible(visible);
      this.map.render();
    }
  }

  // Set BSRI layer opacity
  setBSRILayerOpacity(opacity: number) {
    if (this.bsriLayer) {
      this.bsriLayer.setOpacity(opacity);
      this.map.render();
    }
  }

  // Set LST Philippines layer opacity
  setLSTPHLayerOpacity(opacity: number) {
    if (this.lstPHLayer) {
      this.lstPHLayer.setOpacity(opacity);
      this.map.render();
    }
  }

  // Set VHI Philippines layer opacity
  setVHIPHLayerOpacity(opacity: number) {
    if (this.vhiPHLayer) {
      this.vhiPHLayer.setOpacity(opacity);
      this.map.render();
    }
  }

  // Get LST Philippines legend info (uses vis_params when present, similar to BSRI)
  getLSTPHLegendInfo(): { colors: string[]; labels: string[]; description: string; min: number; max: number } | null {
    if (!this.lstPHData || !this.lstPHData.tiles || this.lstPHData.tiles.length === 0) {
      return null;
    }

    const visParams = this.lstPHData.vis_params;
    const palette = visParams?.palette || [];
    const min = visParams?.min !== undefined ? visParams.min : 0;
    const max = visParams?.max !== undefined ? visParams.max : 50;

    const colors = palette.length > 0 ? palette : ['#313695', '#4575b4', '#74add1', '#abd9e9', '#fee090', '#f46d43', '#a50026'];

    const numStops = colors.length;
    const labels = [];
    for (let i = 0; i < numStops; i++) {
      const value = min + (i / Math.max(numStops - 1, 1)) * (max - min);
      labels.push(`${value.toFixed(1)}`);
    }

    return {
      colors,
      labels,
      description: `Land Surface Temperature (°C): ${min.toFixed(1)} - ${max.toFixed(1)}`,
      min,
      max
    };
  }

  // Get VHI Philippines legend info (uses vis_params when present)
  getVHIPHLegendInfo(): { colors: string[]; labels: string[]; description: string; min: number; max: number } | null {
    if (!this.vhiPHData || !this.vhiPHData.tiles || this.vhiPHData.tiles.length === 0) {
      return null;
    }

    const visParams = this.vhiPHData.vis_params;
    const palette = visParams?.palette || ['red', 'yellow', 'green'];
    const min = visParams?.min !== undefined ? visParams.min : 0;
    const max = visParams?.max !== undefined ? visParams.max : 1;

    const colors = palette;
    const numStops = colors.length;
    const labels: string[] = [];
    for (let i = 0; i < numStops; i++) {
      const value = min + (i / Math.max(numStops - 1, 1)) * (max - min);
      labels.push(value.toFixed(2));
    }

    return {
      colors,
      labels,
      description: `Vegetation Health Index (0–1): ${min.toFixed(2)} - ${max.toFixed(2)}`,
      min,
      max
    };
  }

  // Get BSRI legend info (uses vis_params when present, otherwise sensible defaults so legend is always visible)
  getBSRILegendInfo(): { colors: string[]; labels: string[]; description: string; min: number; max: number } | null {
    if (!this.bsriData || !this.bsriData.tiles || this.bsriData.tiles.length === 0) {
      return null;
    }

    const visParams = this.bsriData.vis_params;
    const palette = visParams?.palette || [];
    const min = visParams?.min !== undefined ? visParams.min : 0;
    const max = visParams?.max !== undefined ? visParams.max : 5;

    // Create color stops for the legend (default viridis-like scale when no palette)
    const colors = palette.length > 0 ? palette : ['#440154', '#31688e', '#35b779', '#fde725'];

    // Create labels based on min/max values distributed evenly across the palette
    const numStops = colors.length;
    const labels = [];
    for (let i = 0; i < numStops; i++) {
      const value = min + (i / Math.max(numStops - 1, 1)) * (max - min);
      labels.push(`${value.toFixed(1)}`);
    }

    return {
      colors: colors,
      labels: labels,
      description: `Black Sigatoka Risk Index: ${min.toFixed(1)} - ${max.toFixed(1)}`,
      min: min,
      max: max
    };
  }

  // Update Straw Burn Detection layer
  updateStrawBurnLayer(strawBurnData: any, opacity: number = 1) {
    // Remove existing straw burn layer
    if (this.strawBurnLayer) {
      this.map.removeLayer(this.strawBurnLayer);
      this.strawBurnLayer = null;
    }

    // If no data, just return
    if (!strawBurnData || !strawBurnData.tile_url) {
      this.strawBurnData = null;
      return;
    }

    // Store data for reference
    this.strawBurnData = strawBurnData;

    const tileUrl = strawBurnData.tile_url;

    // Create new straw burn tile layer
    this.strawBurnLayer = new TileLayer({
      source: new XYZ({
        url: tileUrl,
        crossOrigin: 'anonymous',
      }),
      opacity: opacity,
      zIndex: 56, // Above BSRI layer
    });

    this.map.addLayer(this.strawBurnLayer);
    this.map.render();
  }

  // Toggle Straw Burn Detection layer visibility
  toggleStrawBurnLayer(visible: boolean) {
    if (this.strawBurnLayer) {
      this.strawBurnLayer.setVisible(visible);
      this.map.render();
    }
  }

  // Set Straw Burn Detection layer opacity
  setStrawBurnLayerOpacity(opacity: number) {
    if (this.strawBurnLayer) {
      this.strawBurnLayer.setOpacity(opacity);
      this.map.render();
    }
  }

  // Get Straw Burn Detection legend info
  getStrawBurnLegendInfo(): { colors: string[]; labels: string[]; description: string; min: number; max: number } | null {
    if (!this.strawBurnData || !this.strawBurnData.vis_params) {
      return null;
    }

    const visParams = this.strawBurnData.vis_params;
    const palette = visParams.palette || [];
    const min = visParams.min !== undefined ? visParams.min : 0;
    const max = visParams.max !== undefined ? visParams.max : 1;

    // Create color stops for the legend
    const colors = palette.length > 0 ? palette : ['#ffffff', '#ff0000'];

    // Create labels based on min/max values distributed evenly across the palette
    const numStops = colors.length;
    const labels = [];
    for (let i = 0; i < numStops; i++) {
      const value = min + (i / (numStops - 1)) * (max - min);
      labels.push(`${value.toFixed(1)}`);
    }

    return {
      colors: colors,
      labels: labels,
      description: `Burned Area Detection: ${min.toFixed(1)} - ${max.toFixed(1)}`,
      min: min,
      max: max
    };
  }

  // Initialize vector layer for GeoJSON
  initializeVectorLayer(): VectorLayer {
    const vectorSource = new VectorSource();
    this.vectorLayer = new VectorLayer({
      source: vectorSource,
      style: new Style({
        stroke: new Stroke({
          color: '#3B82F6',
          width: 3,
        }),
        fill: new Fill({
          color: 'rgba(59, 130, 246, 0.15)',
        }),
      }),
      zIndex: 1000, // High z-index to ensure field boundaries are visible
    });

    this.map.addLayer(this.vectorLayer);
    return this.vectorLayer;
  }

  private polygonLabelGeometry(feat: Feature): Geometry | undefined {
    const g = feat.getGeometry();
    if (!g) return undefined;
    const t = g.getType();
    if (t === 'Polygon') {
      return (g as Polygon).getInteriorPoint();
    }
    if (t === 'MultiPolygon') {
      const polys = (g as MultiPolygon).getPolygons();
      return polys.length > 0 ? polys[0].getInteriorPoint() : undefined;
    }
    return undefined;
  }

  /**
   * Draw farm polygons + clustered markers (overview of all farms, or an opened farm group).
   *
   * - Zoomed out: nearby farms merge into one bubble with a count
   * - Mid zoom: individual numbered pins
   * - Zoomed in: polygon outlines + full farm / block name labels
   *
   * Pass null / empty FeatureCollection to clear.
   */
  updateFarmGroupLayer(featureCollection: any | null, options?: { fit?: boolean }) {
    const fit = options?.fit !== false;

    if (this.farmGroupLayer) {
      this.map.removeLayer(this.farmGroupLayer);
      this.farmGroupLayer = null;
    }
    if (this.farmClusterLayer) {
      this.map.removeLayer(this.farmClusterLayer);
      this.farmClusterLayer = null;
    }

    const featuresIn =
      featureCollection?.type === 'FeatureCollection'
        ? featureCollection.features
        : Array.isArray(featureCollection?.features)
          ? featureCollection.features
          : null;
    if (!featuresIn || featuresIn.length === 0) {
      this.map.render();
      return;
    }

    try {
      const format = new GeoJSON({
        featureProjection: 'EPSG:3857',
      });
      const olFeatures = format.readFeatures({
        type: 'FeatureCollection',
        features: featuresIn,
      });

      // Fallback marker numbers if upstream omitted them
      const seenFarm = new Map<string, number>();
      let autoMarker = 0;
      const pointFeatures: Feature[] = [];

      olFeatures.forEach((f) => {
        const props = f.getProperties() || {};
        const name = displayFarmName({
          farm_id: props.farm_id,
          farm_name: props.farm_name ?? props.name ?? props.field_name,
          farm_unique_id: props.farm_unique_id,
        });
        f.set('farm_name', String(name));
        if (props.block_label != null) {
          f.set('block_label', shortPolygonLabel(props.block_label, Number(props.block_number ?? 1) - 1));
        } else if (props.block_number != null) {
          f.set('block_label', shortPolygonLabel(`Block ${props.block_number}`, Number(props.block_number) - 1));
        }
        if (props.farm_id != null) f.set('farm_id', props.farm_id);
        if (props.block_index != null) f.set('block_index', props.block_index);
        if (props.block_number != null) f.set('block_number', props.block_number);

        const farmKey = String(props.farm_id ?? name);
        let marker = props.marker_number;
        if (marker == null || marker === '') {
          if (!seenFarm.has(farmKey)) {
            autoMarker += 1;
            seenFarm.set(farmKey, autoMarker);
          }
          marker = seenFarm.get(farmKey);
        }
        f.set('marker_number', Number(marker));
        const riskRaw = props.risk_level;
        const riskLevel: FarmRiskLevel | null =
          riskRaw === 'high' || riskRaw === 'medium' || riskRaw === 'low' ? riskRaw : null;
        f.set('risk_level', riskLevel);
        const isPrimary =
          props.is_marker_primary === true ||
          props.is_marker_primary === 1 ||
          props.block_index == null ||
          Number(props.block_index) === 0;
        f.set('is_marker_primary', isPrimary);

        // One clusterable point per farm (primary block centroid)
        if (isPrimary) {
          const labelGeom = this.polygonLabelGeometry(f);
          if (labelGeom) {
            const pointFeature = new Feature({
              geometry: new Point(labelGeom.getCoordinates()),
            });
            pointFeature.set('farm_id', f.get('farm_id'));
            pointFeature.set('farm_name', f.get('farm_name'));
            pointFeature.set('block_label', f.get('block_label'));
            pointFeature.set('block_index', f.get('block_index'));
            pointFeature.set('block_number', f.get('block_number'));
            pointFeature.set('marker_number', f.get('marker_number'));
            pointFeature.set('risk_level', riskLevel);
            pointFeature.set('is_marker_primary', true);
            pointFeature.set('layer_role', 'farm_overview');
            pointFeatures.push(pointFeature);
          }
        }
      });

      const polygonSource = new VectorSource({ features: olFeatures });
      const labelFontPx = getMapLabelFontSizePx(12);
      /** Close zoom: Block 1/2… Mid zoom: farm name on primary only. Far: pins. */
      const BLOCK_LABEL_RESOLUTION = 5;
      const FARM_NAME_RESOLUTION = 40;
      const LABEL_RESOLUTION = BLOCK_LABEL_RESOLUTION;

      const polygonStyleFn = (feature: Feature, resolution: number) => {
        const closeIn = resolution <= BLOCK_LABEL_RESOLUTION;
        const showFarmName =
          !closeIn &&
          resolution <= FARM_NAME_RESOLUTION &&
          feature.get('is_marker_primary') === true;
        const riskLevel = feature.get('risk_level') as FarmRiskLevel | null;
        const sym = riskMapSymbology(riskLevel);
        const fillStroke = new Style({
          fill: new Fill({
            color: closeIn ? sym.polyFillClose : sym.polyFillFar,
          }),
          stroke: new Stroke({
            color: closeIn ? sym.polyStrokeClose : sym.polyStrokeFar,
            width: closeIn ? 2.5 : 1.2,
          }),
        });
        if (!closeIn && !showFarmName) return fillStroke;

        const label = closeIn
          ? String(feature.get('block_label') || feature.get('farm_name') || 'Farm')
          : String(feature.get('farm_name') || 'Farm');
        return [
          fillStroke,
          new Style({
            geometry: (feat) => this.polygonLabelGeometry(feat as Feature),
            text: new Text({
              text: label,
              font: `bold ${labelFontPx}px "Poppins", system-ui, sans-serif`,
              fill: new Fill({ color: '#ecfdf5' }),
              stroke: new Stroke({
                color: '#065f46',
                width: Math.max(2, Math.round((3 * labelFontPx) / 12)),
              }),
              overflow: true,
              textAlign: 'center',
              padding: [2, 4, 2, 4],
            }),
          }),
        ];
      };

      this.farmGroupLayer = new VectorLayer({
        source: polygonSource,
        style: polygonStyleFn,
        zIndex: 54,
        declutter: false,
      });
      this.farmGroupLayer.set('layerType', 'farmOverviewPolygons');
      this.map.addLayer(this.farmGroupLayer);

      if (pointFeatures.length > 0) {
        const clusterSource = new Cluster({
          distance: 48,
          minDistance: 18,
          source: new VectorSource({ features: pointFeatures }),
        });

        const clusterStyleFn = (feature: Feature, resolution: number) => {
          const members = (feature.get('features') as Feature[] | undefined) || [];
          const size = members.length;
          // Hide pins once polygons show full labels (close zoom)
          if (resolution <= LABEL_RESOLUTION) return [];

          if (size > 1) {
            const clusterRisk = worstFarmRiskLevel(
              members.map((m) => m.get('risk_level') as FarmRiskLevel | null)
            );
            const sym = riskMapSymbology(clusterRisk);
            const radius = Math.min(28, 12 + Math.log2(size) * 5);
            return new Style({
              image: new Circle({
                radius,
                fill: new Fill({ color: sym.pinFill }),
                stroke: new Stroke({ color: sym.pinStroke, width: 2.5 }),
              }),
              text: new Text({
                text: String(size),
                font: `bold ${size > 99 ? 11 : 13}px "Poppins", system-ui, sans-serif`,
                fill: new Fill({ color: '#ffffff' }),
                textAlign: 'center',
                offsetY: 1,
              }),
            });
          }

          const single = members[0];
          const markerNum = String(single?.get('marker_number') ?? '');
          const sym = riskMapSymbology(single?.get('risk_level') as FarmRiskLevel | null);
          return new Style({
            image: new Circle({
              radius: markerNum.length > 2 ? 13 : 11,
              fill: new Fill({ color: sym.pinFill }),
              stroke: new Stroke({ color: sym.pinStroke, width: 2 }),
            }),
            text: new Text({
              text: markerNum || '•',
              font: `bold ${markerNum.length > 2 ? 10 : 11}px "Poppins", system-ui, sans-serif`,
              fill: new Fill({ color: '#ffffff' }),
              textAlign: 'center',
              offsetY: 1,
            }),
          });
        };

        this.farmClusterLayer = new VectorLayer({
          source: clusterSource,
          style: clusterStyleFn,
          zIndex: 56,
          declutter: false,
        });
        this.farmClusterLayer.set('layerType', 'farmOverviewClusters');
        this.map.addLayer(this.farmClusterLayer);
      }

      if (fit) {
        const extent = polygonSource.getExtent();
        if (extent.every((x) => Number.isFinite(x))) {
          this.map.getView().fit(extent, {
            padding: [72, 72, 72, 340],
            duration: 900,
            maxZoom: 16,
          });
        }
      }
      this.map.render();
    } catch (error) {
      console.error('MapLayerManager: Error updating farm group layer:', error);
      this.map.render();
    }
  }

  /** Draw or clear the LGU monitoring-area boundary outline. */
  updateMonitoringAreaBoundaryLayer(feature: any | null) {
    if (this.monitoringAreaBoundaryLayer) {
      this.map.removeLayer(this.monitoringAreaBoundaryLayer);
      this.monitoringAreaBoundaryLayer = null;
    }

    if (!feature || typeof feature !== 'object') {
      this.map.render();
      return;
    }

    try {
      const format = new GeoJSON({ featureProjection: 'EPSG:3857' });
      const olFeature = format.readFeature(feature);
      const source = new VectorSource({ features: [olFeature] });
      const labelFontPx = getMapLabelFontSizePx(13);
      const label =
        String(feature?.properties?.label || feature?.properties?.name || 'Monitoring area').trim() ||
        'Monitoring area';

      this.monitoringAreaBoundaryLayer = new VectorLayer({
        source,
        zIndex: 48,
        style: [
          new Style({
            fill: new Fill({ color: 'rgba(56, 189, 248, 0.08)' }),
            stroke: new Stroke({
              color: 'rgba(14, 165, 233, 0.95)',
              width: 3,
              lineDash: [12, 8],
            }),
          }),
          new Style({
            geometry: (feat) => this.polygonLabelGeometry(feat as Feature),
            text: new Text({
              text: label,
              font: `600 ${labelFontPx}px "Poppins", system-ui, sans-serif`,
              fill: new Fill({ color: '#0c4a6e' }),
              stroke: new Stroke({
                color: 'rgba(255, 255, 255, 0.92)',
                width: Math.max(3, Math.round((4 * labelFontPx) / 13)),
              }),
              overflow: true,
              textAlign: 'center',
              padding: [3, 6, 3, 6],
            }),
          }),
        ],
      });
      this.monitoringAreaBoundaryLayer.set('layerType', 'lguMonitoringBoundary');
      this.map.addLayer(this.monitoringAreaBoundaryLayer);
      this.map.render();
    } catch (error) {
      console.error('MapLayerManager: Error updating monitoring area boundary:', error);
      this.map.render();
    }
  }

  clearMonitoringAreaBoundaryLayer() {
    this.updateMonitoringAreaBoundaryLayer(null);
  }

  getFarmOverviewLayer() {
    return this.farmClusterLayer ?? this.farmGroupLayer;
  }

  /** Polygon outlines + cluster pins (for hit-testing). */
  getFarmOverviewLayers(): VectorLayer<VectorSource>[] {
    return [this.farmClusterLayer, this.farmGroupLayer].filter(
      (layer): layer is VectorLayer<VectorSource> => layer != null
    );
  }

  /**
   * If ``feature`` is a multi-farm cluster, zoom into its members.
   * Returns true when handled (caller should not open a single farm).
   */
  zoomIntoFarmCluster(feature: Feature): boolean {
    const members = feature.get('features');
    if (!Array.isArray(members) || members.length <= 1) return false;

    const extent = createEmpty();
    for (const member of members) {
      const geom = (member as Feature).getGeometry();
      if (!geom) continue;
      extend(extent, geom.getExtent());
    }
    if (isEmpty(extent)) return false;

    this.map.getView().fit(extent, {
      padding: [80, 80, 80, 340],
      duration: 550,
      maxZoom: 17,
    });
    return true;
  }

  clearFarmGroupLayer() {
    this.updateFarmGroupLayer(null);
  }

  // Update GeoJSON visualization
  updateGeoJsonLayer(fieldGeoJson: any, selectedField?: any) {
    if (!this.vectorLayer) return;

    const vectorSource = this.vectorLayer.getSource();
    if (!vectorSource) return;

    // Must clear when geometry is removed (e.g. delete unsaved drawn field); early return used to leave stale features on the map.
    if (!fieldGeoJson) {
      vectorSource.clear();
      return;
    }

    try {
      vectorSource.clear();

      const format = new GeoJSON({
        featureProjection: 'EPSG:3857',
      });

      const farmName =
        selectedField?.farm_name ?? selectedField?.name ?? selectedField?.field_name ?? 'Field';

      // Normalize to per-block Polygon features so close-zoom can show Block 1 / Block 2.
      let featuresIn: Array<{
        type: 'Feature';
        properties: Record<string, unknown>;
        geometry: { type: string; coordinates: unknown };
      }> = [];
      const root = fieldGeoJson?.type === 'Feature' ? fieldGeoJson : fieldGeoJson;
      const geom =
        root?.type === 'Feature'
          ? root.geometry
          : root?.type === 'FeatureCollection'
            ? null
            : root?.geometry ?? root;

      if (root?.type === 'FeatureCollection' && Array.isArray(root.features)) {
        featuresIn = root.features.map((f: any, i: number) => ({
          type: 'Feature' as const,
          properties: {
            ...(f.properties || {}),
            farm_name: farmName,
            block_number: (f.properties?.block_number as number) ?? i + 1,
            block_label: shortPolygonLabel(
              (f.properties?.block_label as string) ||
                (f.properties?.name as string) ||
                (f.properties?.field_name as string),
              ((f.properties?.block_number as number) ?? i + 1) - 1,
            ),
          },
          geometry: f.geometry,
        }));
      } else if (geom?.type === 'MultiPolygon' && Array.isArray(geom.coordinates)) {
        featuresIn = geom.coordinates.map((poly: unknown, i: number) => ({
          type: 'Feature' as const,
          properties: {
            farm_name: farmName,
            block_index: i,
            block_number: i + 1,
            block_label: String(i + 1),
            is_marker_primary: i === 0,
          },
          geometry: { type: 'Polygon', coordinates: poly },
        }));
      } else if (geom?.type === 'Polygon') {
        featuresIn = [
          {
            type: 'Feature',
            properties: {
              farm_name: farmName,
              block_number: 1,
              block_label: farmName,
              is_marker_primary: true,
            },
            geometry: geom,
          },
        ];
      } else {
        const featureOrFeatures = format.readFeatures(fieldGeoJson);
        const features = Array.isArray(featureOrFeatures) ? featureOrFeatures : [featureOrFeatures];
        for (const feature of features) {
          if (!feature) continue;
          if (selectedField) {
            feature.set('farm_id', selectedField.farm_id);
            feature.set('farm_name', farmName);
            feature.set('isDemo', !!selectedField.isDemo);
          }
          feature.setStyle(
            new Style({
              stroke: new Stroke({ color: '#3B82F6', width: selectedField ? 4 : 3 }),
              fill: new Fill({
                color: selectedField ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.15)',
              }),
            })
          );
          vectorSource.addFeature(feature);
        }
        if (selectedField && features.length > 0 && this.map) {
          const extent = vectorSource.getExtent();
          if (extent && extent.every((v) => Number.isFinite(v))) {
            this.map.getView().fit(extent, {
              padding: [50, 50, 50, 50],
              maxZoom: 18,
              duration: 1000,
            });
          }
        }
        return;
      }

      const olFeatures = format.readFeatures({
        type: 'FeatureCollection',
        features: featuresIn,
      });
      // Outline only. A name in the middle sits on top of the index map and other analysis layers.
      const outlineStyle = new Style({
        stroke: new Stroke({
          color: '#3B82F6',
          width: selectedField ? 4 : 3,
        }),
        fill: new Fill({
          color: selectedField ? 'rgba(59, 130, 246, 0.12)' : 'rgba(59, 130, 246, 0.15)',
        }),
      });

      for (const feature of olFeatures) {
        if (!feature) continue;
        if (selectedField) {
          feature.set('farm_id', selectedField.farm_id);
          feature.set('farm_name', farmName);
          feature.set('isDemo', !!selectedField.isDemo);
        }
        feature.setStyle(outlineStyle);
        vectorSource.addFeature(feature);
      }

      // Auto-fit to all blocks when a field is selected
      if (selectedField && olFeatures.length > 0 && this.map) {
        const extent = vectorSource.getExtent();
        if (extent && extent.every((v) => Number.isFinite(v))) {
          this.map.getView().fit(extent, {
            padding: [50, 50, 50, 50],
            maxZoom: 18,
            duration: 1000,
          });
        }
      }
    } catch (error) {
      console.error('Error adding GeoJSON to map:', error);
    }
  }

  // Toggle WMS layer visibility — only NDVI_Yield (GeoServer); VHI/LST use GEE tiles via PH APIs
  toggleWMSLayer(layerType: 'yield' | 'vhi' | 'lst', visible: boolean) {
    if (layerType !== 'yield') {
      return;
    }
    const targetLayer = this.yieldLayer;
    if (!targetLayer) {
      return;
    }

    if (visible) {
      // Show the target layer
      targetLayer.setVisible(true);
      
      // Set zoom to level 9 when showing any WMS layer
      
      this.map.getView().animate({
        zoom: 9,
        duration: 500
      });
      
      // Force refresh the layer to trigger network requests
      const source = targetLayer.getSource() as ImageWMS;
      if (source) {
        // Debug yield layer specifically
        if (layerType === 'yield') {
          const currentParams = source.getParams();
          
          // Force update params to trigger refresh
          source.updateParams({
            ...currentParams,
            '_refresh': Date.now()
          });
        }
        
        // Force refresh
        source.refresh();
      }
    } else {
      // Simply hide the layer
      targetLayer.setVisible(false);
    }
    
    // Trigger map render
    this.map.render();
  }

  // Update WMS layer time parameter with enhanced cache busting
  updateWMSLayerTime(layerType: 'yield' | 'vhi' | 'lst', dateTime: string) {
    if (layerType !== 'yield') {
      return;
    }
    const layer = this.yieldLayer;
    if (layer && layer.getSource()) {
      const source = layer.getSource() as ImageWMS;
      
      const updateParams: any = { 'TIME': dateTime };
      
      // Update the TIME parameter and cache busters
      source.updateParams(updateParams);
      
      // Force refresh to trigger new request
      source.refresh();
      
      // Trigger map render
      this.map.render();
    } else {
      console.error(`MapLayerManager: Cannot update time for ${layerType} layer - layer or source not found`);
    }
  }

  // Set WMS layer opacity
  setWMSLayerOpacity(layerType: 'yield' | 'vhi' | 'lst', opacity: number) {
    if (layerType !== 'yield' || !this.yieldLayer) {
      return;
    }
    this.yieldLayer.setOpacity(opacity);
    this.map.render();
  }

  // Get WMS legend URL (yield only; VHI/LST legends come from GEE/API in MapView)
  getWMSLegendUrl(layerType: 'yield' | 'vhi' | 'lst'): string {
    if (layerType !== 'yield') {
      return '';
    }
    const config = this.wmsConfigs.yield;
    return `${config.url}?REQUEST=GetLegendGraphic&FORMAT=image/png&WIDTH=10&HEIGHT=10&LAYER=${config.layers}&STYLE=${config.styles}`;
  }

  // NEW: Get crop area legend info (color and label)
  getCropAreaLegendInfo(cropType: CropAreaLayerKey): { colors: string[]; labels: string[]; description: string; totalArea?: number } | null {
    if (cropType === 'coconut') {
      return { ...COCONUT_CROP_LEGEND };
    }
    if (cropType === 'maize') {
      return { ...MAIZE_CROP_LEGEND };
    }
    if (cropType === 'rice') {
      return { ...RICE_CROP_LEGEND };
    }
    if (cropType === 'sugarCane') {
      return { ...SUGARCANE_CROP_LEGEND };
    }
    return null;
  }

  getDroneNDVILegendUrl(_layerName?: string): string {
    return '';
  }

  // Check if any GeoServer WMS layer is visible (yield only)
  isAnyWMSLayerVisible(): boolean {
    return this.yieldLayer?.getVisible() ?? false;
  }

  getActiveWMSLayer(): 'yield' | null {
    if (this.yieldLayer?.getVisible()) return 'yield';
    return null;
  }

  // UPDATED: Get current layer visibility state (includes crop area layers and drone NDVI)
  getLayerVisibility(): LayerVisibilityState {
    return {
      yield: this.yieldLayer?.getVisible() ?? false,
      vhi: this.vhiPHLayer?.getVisible() ?? false,
      lst: this.lstPHLayer?.getVisible() ?? false,
      heatMap: (this.heatMapLayer?.getOpacity() ?? 0) > 0,
      timeSeries: (this.timeSeriesLayer?.getOpacity() ?? 0) > 0,
      // New crop area layers
      maize: this.maizeLayer?.getVisible() ?? false,
      sugarCane: this.sugarCaneLayer?.getVisible() ?? false,
      rice: this.riceLayer?.getVisible() ?? false,
      coconut: this.coconutLayer?.getVisible() ?? false,
      droneNDVI: false,
      // Rice forecast layer
      riceForecast: this.riceForecastLayer?.getVisible() ?? false,
      treeDetection: this.treeDetectionLayer?.getVisible() ?? false,
      // BSRI layer
      bsri: this.bsriLayer?.getVisible() ?? false,
      // Straw Burn layer
      strawBurn: this.strawBurnLayer?.getVisible() ?? false,
      // Planting Method Map layers (nationwide Philippines)
      plantingMethodDry2025: this.plantingMethodDry2025Layer?.getVisible() ?? false,
      plantingMethodDry2024: this.plantingMethodDry2024Layer?.getVisible() ?? false,
      riceGrowthStage: this.riceGrowthStageLayer?.getVisible() ?? false,
      irrigationType: this.irrigationTypeLayer?.getVisible() ?? false,
      cadiDrought: this.cadiDroughtLayer?.getVisible() ?? false,
      pestRisk: this.pestRiskLayer?.getVisible() ?? false,
      // Isabela PhilRice drone COGs
      isabelaPhilriceNDVI: this.isabelaPhilriceNDVILayer?.getVisible() ?? false,
      isabelaPhilriceOrtho: this.isabelaPhilriceOrthoLayer?.getVisible() ?? false,
      caviteSilangNDVI: this.caviteSilangNDVILayer?.getVisible() ?? false,
      caviteSilangOrtho: this.caviteSilangOrthoLayer?.getVisible() ?? false,
    };
  }

  // UPDATED: Get current layer opacity state (includes crop area layers and drone NDVI)
  getLayerOpacity(): LayerOpacityState {
    return {
      yield: this.yieldLayer?.getOpacity() ?? 1,
      vhi: this.vhiPHLayer?.getOpacity() ?? 1,
      lst: this.lstPHLayer?.getOpacity() ?? 1,
      heatMap: this.heatMapLayer?.getOpacity() ?? 1,
      timeSeries: this.timeSeriesLayer?.getOpacity() ?? 1,
      // New crop area layers
      maize: this.maizeLayer?.getOpacity() ?? 1,
      sugarCane: this.sugarCaneLayer?.getOpacity() ?? 1,
      rice: this.riceLayer?.getOpacity() ?? 1,
      coconut: this.coconutLayer?.getOpacity() ?? 1,
      droneNDVI: 1,
      // Rice forecast layer
      riceForecast: this.riceForecastLayer?.getOpacity() ?? 1,
      treeDetection: this.treeDetectionLayer?.getOpacity() ?? 1,
      // BSRI layer
      bsri: this.bsriLayer?.getOpacity() ?? 1,
      // Straw Burn layer
      strawBurn: this.strawBurnLayer?.getOpacity() ?? 1,
      // Planting Method Map layers (nationwide Philippines)
      plantingMethodDry2025: this.plantingMethodDry2025Layer?.getOpacity() ?? 1,
      plantingMethodDry2024: this.plantingMethodDry2024Layer?.getOpacity() ?? 1,
      riceGrowthStage: this.riceGrowthStageLayer?.getOpacity() ?? 1,
      irrigationType: this.irrigationTypeLayer?.getOpacity() ?? 1,
      cadiDrought: this.cadiDroughtLayer?.getOpacity() ?? 1,
      pestRisk: this.pestRiskLayer?.getOpacity() ?? 1,
      // Isabela PhilRice drone COGs
      isabelaPhilriceNDVI: this.isabelaPhilriceNDVILayer?.getOpacity() ?? 1,
      isabelaPhilriceOrtho: this.isabelaPhilriceOrthoLayer?.getOpacity() ?? 1,
      caviteSilangNDVI: this.caviteSilangNDVILayer?.getOpacity() ?? 1,
      caviteSilangOrtho: this.caviteSilangOrthoLayer?.getOpacity() ?? 1,
    };
  }

  // Hide all GeoServer WMS layers (yield only)
  hideAllWMSLayers() {
    if (this.yieldLayer?.getVisible()) {
      this.yieldLayer.setVisible(false);
    }
    this.map.render();
  }

  // Debug method to inspect current WMS layer states (no console output)
  debugWMSLayers() {
    // No-op; kept for API compatibility / breakpoint debugging
  }

  // Force refresh all visible WMS layers (utility method)
  refreshAllVisibleWMSLayers() {
    if (this.yieldLayer?.getVisible()) {
      const source = this.yieldLayer.getSource() as ImageWMS;
      source?.refresh();
    }
  }

  // Test yield layer specifically
  testYieldLayer() {
    if (!this.yieldLayer) {
      console.error('Yield layer not initialized!');
      return;
    }

    const source = this.yieldLayer.getSource() as ImageWMS;
    if (!source) {
      console.error('Yield layer source not found!');
      return;
    }

    source.refresh();
    this.map.render();
  }

  // Fix yield layer - recreate if necessary
  fixYieldLayer() {
    // Remove existing yield layer if it exists
    if (this.yieldLayer) {
      this.map.removeLayer(this.yieldLayer);
    }

    // Recreate yield layer
    this.yieldLayer = new ImageLayer({
      source: new ImageWMS({
        url: this.wmsConfigs.yield.url,
        params: {
          'LAYERS': this.wmsConfigs.yield.layers,
          'FORMAT': 'image/png',
          'TRANSPARENT': true,
          'TIME': this.wmsConfigs.yield.defaultTime,
          'STYLES': this.wmsConfigs.yield.styles || '',
          'VERSION': '1.1.1',
          'SRS': 'EPSG:3857'
        },
        serverType: 'geoserver',
        crossOrigin: 'anonymous'
      }),
      visible: false,
      opacity: 1,
    });
    
    this.yieldLayer.setZIndex(100);
    this.map.addLayer(this.yieldLayer);
  }

  // Update heat map layer
  updateHeatMapLayer(indexHeatMapData: any, opacity: number = 1) {
    // Remove existing heat map layer
    if (this.heatMapLayer) {
      this.map.removeLayer(this.heatMapLayer);
      this.heatMapLayer = null;
    }

    // Add new heat map layer if we have tile URL
    if (indexHeatMapData?.tileUrl) {
      this.heatMapLayer = new TileLayer({
        source: new XYZ({
          url: indexHeatMapData.tileUrl,
          crossOrigin: 'anonymous',
        }),
        opacity: opacity,
        zIndex: 500,
      });

      this.map.addLayer(this.heatMapLayer);
    }
  }

  // Update time series layer
  updateTimeSeriesLayer(imageData: any, opacity: number = 1) {
    // Remove existing time series layer
    if (this.timeSeriesLayer) {
      this.map.removeLayer(this.timeSeriesLayer);
      this.timeSeriesLayer = null;
    }

    // Add new time series layer
    if (imageData?.tile_url) {
      this.timeSeriesLayer = new TileLayer({
        source: new XYZ({
          url: imageData.tile_url,
          crossOrigin: 'anonymous',
        }),
        opacity: opacity,
        zIndex: 500,
        visible: !this.changeDetectionActive,
      });

      this.map.addLayer(this.timeSeriesLayer);
    }
  }

  // Toggle layer visibility
  toggleLayerVisibility(layerType: 'heatMap' | 'timeSeries', originalOpacity: number) {
    const layer = layerType === 'heatMap' ? this.heatMapLayer : this.timeSeriesLayer;
    if (layer) {
      const currentOpacity = layer.getOpacity();
      const newOpacity = currentOpacity > 0 ? 0 : originalOpacity;
      layer.setOpacity(newOpacity);
    }
  }

  // Adjust layer opacity
  adjustLayerOpacity(layerType: 'heatMap' | 'timeSeries', opacity: number) {
    const layer = layerType === 'heatMap' ? this.heatMapLayer : this.timeSeriesLayer;
    if (layer) {
      layer.setOpacity(opacity);
    }
  }

  // Change base layer
  changeBaseLayer(newBaseLayer: 'satellite' | 'hybrid' | 'terrain' | 'googleSatellite' | 'googleHybrid' | 'googleTerrain') {
    const layers = this.map.getLayers().getArray();

    // Find and remove the current base layer using marker property
    // This is more reliable than checking constructor names or source types
    let baseLayerFound = false;
    for (let i = 0; i < layers.length; i++) {
      const layer = layers[i];

      // Check if this layer is marked as a base layer
      if (layer.get('layerType') === 'baseLayer') {
        this.map.removeLayer(layer);
        baseLayerFound = true;
        break;
      }
    }

    if (!baseLayerFound) {
      // Fallback: remove layer at index 0 if it exists
      if (layers.length > 0) {
        this.map.removeLayer(layers[0]);
      }
    }

    // Create and add the new base layer
    const baseLayers = MapLayerManager.createBaseLayers();
    const newLayer = baseLayers[newBaseLayer];

    // Set z-index to ensure it's at the bottom
    newLayer.setZIndex(0);

    this.map.getLayers().insertAt(0, newLayer);

    // Force map to render the new layer
    this.map.render();
  }

  // Zoom to field extent
  zoomToField() {
    if (this.vectorLayer) {
      const source = this.vectorLayer.getSource();
      const features = source?.getFeatures();
      if (features && features.length > 0) {
        const extent = features[0].getGeometry()?.getExtent();
        if (extent) {
          this.map.getView().fit(extent, {
            padding: [50, 50, 50, 50],
            maxZoom: 18,
            duration: 1000,
          });
        }
      }
    }
  }

  // Get vector layer
  getVectorLayer(): VectorLayer | null {
    return this.vectorLayer;
  }

  // Get heat map layer
  getHeatMapLayer(): TileLayer | null {
    return this.heatMapLayer;
  }

  // Get time series layer
  getTimeSeriesLayer(): TileLayer | null {
    return this.timeSeriesLayer;
  }

  // GeoServer drone-data WMS removed — use COG/TiTiler mission layers in LayerTogglePanel
  toggleDroneNDVILayer(_visible: boolean, _layerName: string, _opacity: number = 1) {
    this.map.render();
  }

  setDroneNDVILayerOpacity(_opacity: number) {
    this.map.render();
  }

  isDroneNDVILayerVisible(): boolean {
    return false;
  }

  // Clip a change-detection layer to one side of the swipe line.
  // getRenderPixel converts the line's CSS position into canvas pixels (retina-safe).
  private clipChangeDetectionSide(event: any, side: 'before' | 'after') {
    const ctx = event.context as CanvasRenderingContext2D | undefined;
    const mapSize = this.map.getSize();
    if (!ctx || !mapSize || typeof ctx.save !== 'function') return;

    const splitX = mapSize[0] * (this.swipePosition / 100);
    const [x0, x1] = side === 'before' ? [0, splitX] : [splitX, mapSize[0]];
    const topLeft = getRenderPixel(event, [x0, 0]);
    const topRight = getRenderPixel(event, [x1, 0]);
    const bottomRight = getRenderPixel(event, [x1, mapSize[1]]);
    const bottomLeft = getRenderPixel(event, [x0, mapSize[1]]);

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(topLeft[0], topLeft[1]);
    ctx.lineTo(bottomLeft[0], bottomLeft[1]);
    ctx.lineTo(bottomRight[0], bottomRight[1]);
    ctx.lineTo(topRight[0], topRight[1]);
    ctx.closePath();
    ctx.clip();
  }

  // Change Detection Methods - Split-Screen Swipe
  setupChangeDetectionLayers(beforeImageData: any, afterImageData: any, opacity: number = 1) {
    // Remove existing change detection layers
    this.clearChangeDetectionLayers();

    if (!beforeImageData?.tile_url || !afterImageData?.tile_url) {
      console.warn('MapLayerManager: Missing tile URLs for change detection');
      return;
    }

    this.changeDetectionActive = true;
    if (this.timeSeriesLayer) {
      this.timeSeriesLayer.setVisible(false);
    }

    // Create before layer (left side)
    this.changeDetectionBeforeLayer = new TileLayer({
      source: new XYZ({
        url: beforeImageData.tile_url,
        crossOrigin: 'anonymous',
      }),
      opacity: opacity,
      zIndex: 501,
    });

    // Create after layer (right side)
    this.changeDetectionAfterLayer = new TileLayer({
      source: new XYZ({
        url: afterImageData.tile_url,
        crossOrigin: 'anonymous',
      }),
      opacity: opacity,
      zIndex: 502,
    });

    // Set up clipping event handlers using arrow functions to maintain 'this' context
    this.beforePreRenderKey = this.changeDetectionBeforeLayer.on('prerender', (event: any) => {
      this.clipChangeDetectionSide(event, 'before');
    });

    this.beforePostRenderKey = this.changeDetectionBeforeLayer.on('postrender', (event: any) => {
      const ctx = event.context;
      ctx?.restore?.();
    });

    this.afterPreRenderKey = this.changeDetectionAfterLayer.on('prerender', (event: any) => {
      this.clipChangeDetectionSide(event, 'after');
    });

    this.afterPostRenderKey = this.changeDetectionAfterLayer.on('postrender', (event: any) => {
      const ctx = event.context;
      ctx.restore();
    });

    // Add layers to map
    this.map.addLayer(this.changeDetectionBeforeLayer);
    this.map.addLayer(this.changeDetectionAfterLayer);
    this.map.render();
  }

  updateSwipePosition(position: number) {
    this.swipePosition = position;

    if (!this.changeDetectionBeforeLayer || !this.changeDetectionAfterLayer) {
      return;
    }

    // Force map re-render to apply the new swipe position
    this.map.render();
  }

  clearChangeDetectionLayers() {
    this.changeDetectionActive = false;
    if (this.timeSeriesLayer) {
      this.timeSeriesLayer.setVisible(true);
    }
    // Unregister event listeners
    if (this.changeDetectionBeforeLayer) {
      if (this.beforePreRenderKey) {
        this.changeDetectionBeforeLayer.un('prerender', this.beforePreRenderKey);
        this.beforePreRenderKey = null;
      }
      if (this.beforePostRenderKey) {
        this.changeDetectionBeforeLayer.un('postrender', this.beforePostRenderKey);
        this.beforePostRenderKey = null;
      }
      this.map.removeLayer(this.changeDetectionBeforeLayer);
      this.changeDetectionBeforeLayer = null;
    }

    if (this.changeDetectionAfterLayer) {
      if (this.afterPreRenderKey) {
        this.changeDetectionAfterLayer.un('prerender', this.afterPreRenderKey);
        this.afterPreRenderKey = null;
      }
      if (this.afterPostRenderKey) {
        this.changeDetectionAfterLayer.un('postrender', this.afterPostRenderKey);
        this.afterPostRenderKey = null;
      }
      this.map.removeLayer(this.changeDetectionAfterLayer);
      this.changeDetectionAfterLayer = null;
    }
  }

  setRiceGrowthStageLoadingListener(listener: ((loading: boolean) => void) | null) {
    this.riceGrowthStageLoadingListener = listener;
  }

  toggleRiceGrowthStageLayer(
    visible: boolean,
    opacity: number = 1,
    cogUrl?: string,
    bbox: [number, number, number, number] = RICE_GROWTH_STAGE_BBOX
  ): [number, number, number, number] | null {
    if (this.riceGrowthStageLayer) {
      this.map.removeLayer(this.riceGrowthStageLayer);
      this.riceGrowthStageLayer = null;
    }
    if (!visible) {
      this.riceGrowthStageLoadingListener?.(false);
      this.map.render();
      return null;
    }

    const tileUrl = buildRiceGrowthStageTileUrl(cogUrl);
    const extent = transformExtent(bbox, 'EPSG:4326', 'EPSG:3857');
    const source = new XYZ({
      url: tileUrl,
      crossOrigin: 'anonymous',
      tileSize: 512,
      cacheSize: 4096,
      interpolate: false,
      extent,
    });
    let inFlight = 0;
    let sawTile = false;
    const settle = () => {
      if (inFlight === 0 && sawTile) this.riceGrowthStageLoadingListener?.(false);
    };
    source.on('tileloadstart', () => {
      inFlight += 1;
      sawTile = true;
      this.riceGrowthStageLoadingListener?.(true);
    });
    source.on('tileloadend', () => {
      inFlight = Math.max(0, inFlight - 1);
      settle();
    });
    source.on('tileloaderror', () => {
      inFlight = Math.max(0, inFlight - 1);
      settle();
    });
    this.riceGrowthStageLoadingListener?.(true);
    this.riceGrowthStageLayer = new TileLayer({
      source,
      transition: 0,
      visible: true,
      opacity,
      zIndex: 58,
    });
    this.map.addLayer(this.riceGrowthStageLayer);
    this.map.render();
    return bbox;
  }

  setRiceGrowthStageLayerOpacity(opacity: number) {
    if (this.riceGrowthStageLayer) {
      this.riceGrowthStageLayer.setOpacity(opacity);
      this.map.render();
    }
  }

  clearRiceGrowthStageLayer() {
    this.toggleRiceGrowthStageLayer(false);
  }

  setIrrigationTypeLoadingListener(listener: ((loading: boolean) => void) | null) {
    this.irrigationTypeLoadingListener = listener;
  }

  toggleIrrigationTypeLayer(
    visible: boolean,
    opacity: number = 1
  ): [number, number, number, number] | null {
    if (this.irrigationTypeLayer) {
      this.map.removeLayer(this.irrigationTypeLayer);
      this.irrigationTypeLayer = null;
    }
    if (!visible) {
      this.irrigationTypeLoadingListener?.(false);
      this.map.render();
      return null;
    }

    ensureProjectionsRegistered('EPSG:32651');
    const extent = transformExtent(IRRIGATION_TYPE_NATIVE_BBOX, 'EPSG:32651', 'EPSG:3857');
    const source = new XYZ({
      url: buildIrrigationTypeTileUrl(),
      crossOrigin: 'anonymous',
      tileSize: 512,
      cacheSize: 4096,
      interpolate: false,
      extent,
    });
    let inFlight = 0;
    let sawTile = false;
    const settle = () => {
      if (inFlight === 0 && sawTile) this.irrigationTypeLoadingListener?.(false);
    };
    source.on('tileloadstart', () => {
      inFlight += 1;
      sawTile = true;
      this.irrigationTypeLoadingListener?.(true);
    });
    source.on('tileloadend', () => {
      inFlight = Math.max(0, inFlight - 1);
      settle();
    });
    source.on('tileloaderror', () => {
      inFlight = Math.max(0, inFlight - 1);
      settle();
    });
    this.irrigationTypeLoadingListener?.(true);
    this.irrigationTypeLayer = new TileLayer({
      source,
      transition: 0,
      visible: true,
      opacity,
      zIndex: 59,
    });
    this.map.addLayer(this.irrigationTypeLayer);
    this.map.render();
    return IRRIGATION_TYPE_BBOX;
  }

  setIrrigationTypeLayerOpacity(opacity: number) {
    if (this.irrigationTypeLayer) {
      this.irrigationTypeLayer.setOpacity(opacity);
      this.map.render();
    }
  }

  clearIrrigationTypeLayer() {
    this.toggleIrrigationTypeLayer(false);
  }

  // Planting Method Map Layer Methods
  togglePlantingMethodLayer(
    region: 'isabela' | 'laguna',
    visible: boolean,
    opacity: number = 1
  ): [number, number, number, number] | null {
    const TITILER_BASE = DIGISAKA_API_ORIGIN + '/api/tiles/WebMercatorQuad/{z}/{x}/{y}@2x.png';
    const COLORMAP = encodeURIComponent(JSON.stringify({ '0': '#00FFFF', '1': '#FF5500' }));

    const configs: Record<string, { cogUrl: string; layerProp: 'plantingMethodDry2025Layer' | 'plantingMethodDry2024Layer'; bbox: [number, number, number, number] }> = {
      isabela: {
        cogUrl: PLANTING_METHOD_COGS.dry2025,
        layerProp: 'plantingMethodDry2025Layer',
        bbox: [116.928, 4.587, 126.604, 20.117]
      },
      laguna: {
        cogUrl: PLANTING_METHOD_COGS.dry2024,
        layerProp: 'plantingMethodDry2024Layer',
        bbox: [116.928, 4.587, 126.604, 20.117]
      }
    };

    const cfg = configs[region];
    if (!cfg) return null;

    const currentLayer = this[cfg.layerProp];

    if (visible) {
      if (!currentLayer) {
        const tileUrl = `${TITILER_BASE}?url=${encodeURIComponent(cfg.cogUrl)}&bidx=1&colormap=${COLORMAP}&resampling=lanczos`;
        const newLayer = new TileLayer({
          source: new XYZ({
            url: tileUrl,
            crossOrigin: 'anonymous',
            tileSize: 512,
            cacheSize: 4096,
          }),
          transition: 0,
          visible: true,
          opacity: opacity,
          zIndex: 58
        });
        this[cfg.layerProp] = newLayer;
        this.map.addLayer(newLayer);
      } else {
        currentLayer.setVisible(true);
        currentLayer.setOpacity(opacity);
      }
      return cfg.bbox;
    } else {
      if (currentLayer) currentLayer.setVisible(false);
    }
    this.map.render();
    return null;
  }

  setPlantingMethodLayerOpacity(region: 'isabela' | 'laguna', opacity: number) {
    if (region === 'isabela' && this.plantingMethodDry2025Layer) {
      this.plantingMethodDry2025Layer.setOpacity(opacity);
      this.map.render();
    } else if (region === 'laguna' && this.plantingMethodDry2024Layer) {
      this.plantingMethodDry2024Layer.setOpacity(opacity);
      this.map.render();
    }
  }

  // Stub kept for API compatibility – new COG layers use inline Titiler colormaps
  getPlantingMethodLegendUrl(_region: 'isabela' | 'laguna'): string {
    return '';
  }

  updateCADIDroughtLayer(frameIndex: number, opacity: number = 1): void {
    if (this.cadiDroughtLayer) {
      this.map.removeLayer(this.cadiDroughtLayer);
      this.cadiDroughtLayer = null;
    }

    const frame = CADI_DROUGHT_FRAMES[frameIndex];
    if (!frame) {
      this.cadiDroughtFrameIndex = 0;
      return;
    }

    this.cadiDroughtFrameIndex = frameIndex;
    const tileUrl = buildCADIDroughtTileUrl(frame.cogUrl, frame.bidx);
    const cadiExtent = transformExtent(CADI_DROUGHT_BBOX, 'EPSG:4326', 'EPSG:3857');
    this.cadiDroughtLayer = new TileLayer({
      source: new XYZ({
        url: tileUrl,
        crossOrigin: 'anonymous',
        tileSize: 512,
        cacheSize: 4096,
        extent: cadiExtent,
      }),
      transition: 0,
      visible: true,
      opacity,
      zIndex: 57,
    });
    this.map.addLayer(this.cadiDroughtLayer);
    this.map.render();
  }

  clearCADIDroughtLayer(): void {
    if (this.cadiDroughtLayer) {
      this.map.removeLayer(this.cadiDroughtLayer);
      this.cadiDroughtLayer = null;
      this.map.render();
    }
    this.cadiDroughtFrameIndex = 0;
  }

  setCADIDroughtLayerOpacity(opacity: number): void {
    if (this.cadiDroughtLayer) {
      this.cadiDroughtLayer.setOpacity(opacity);
      this.map.render();
    }
  }

  getCADIDroughtLegendInfo(): typeof CADI_DROUGHT_LEGEND | null {
    if (!this.cadiDroughtLayer?.getVisible()) return null;
    return CADI_DROUGHT_LEGEND;
  }

  getCADIDroughtBbox(): [number, number, number, number] {
    return CADI_DROUGHT_BBOX;
  }

  updatePestRiskLayer(
    pestKey: PestRiskPestKey,
    quarterIndex: number,
    opacity: number = 1
  ): void {
    if (this.pestRiskLayer) {
      this.map.removeLayer(this.pestRiskLayer);
      this.pestRiskLayer = null;
    }

    const frames = getPestRiskFrames(pestKey);
    const frame = frames[quarterIndex];
    if (!frame) {
      this.pestRiskPestKey = pestKey;
      this.pestRiskQuarterIndex = 0;
      return;
    }

    this.pestRiskPestKey = pestKey;
    this.pestRiskQuarterIndex = quarterIndex;
    const tileUrl = buildPestRiskTileUrl(frame.cogUrl);
    const extent = transformExtent(PEST_RISK_BBOX, 'EPSG:4326', 'EPSG:3857');
    this.pestRiskLayer = new TileLayer({
      source: new XYZ({
        url: tileUrl,
        crossOrigin: 'anonymous',
        tileSize: 512,
        cacheSize: 4096,
        extent,
      }),
      transition: 0,
      visible: true,
      opacity,
      zIndex: 56,
    });
    this.map.addLayer(this.pestRiskLayer);
    this.map.render();
  }

  clearPestRiskLayer(): void {
    if (this.pestRiskLayer) {
      this.map.removeLayer(this.pestRiskLayer);
      this.pestRiskLayer = null;
      this.map.render();
    }
    this.pestRiskQuarterIndex = 0;
  }

  setPestRiskLayerOpacity(opacity: number): void {
    if (this.pestRiskLayer) {
      this.pestRiskLayer.setOpacity(opacity);
      this.map.render();
    }
  }

  getPestRiskLegendInfo(): typeof PEST_RISK_LEGEND | null {
    if (!this.pestRiskLayer?.getVisible()) return null;
    return PEST_RISK_LEGEND;
  }

  getPestRiskBbox(): [number, number, number, number] {
    return PEST_RISK_BBOX;
  }

  getPestRiskPestKey(): PestRiskPestKey {
    return this.pestRiskPestKey;
  }

  /** Isabela PhilRice COG extent (EPSG:4326) from TiTiler `/api/bounds` — same footprint for mission outputs. */
  private readonly ISABELA_PHILRICE_BBOX: [number, number, number, number] = [
    121.59505679467499, 16.876529833676, 121.59891385851898, 16.879314985676
  ];

  toggleIsabelaPhilriceLayer(
    kind: 'ndvi' | 'ortho',
    visible: boolean,
    opacity: number = 1
  ): [number, number, number, number] | null {
    const layerProps: Record<'ndvi' | 'ortho', 'isabelaPhilriceNDVILayer' | 'isabelaPhilriceOrthoLayer'> = {
      ndvi: 'isabelaPhilriceNDVILayer',
      ortho: 'isabelaPhilriceOrthoLayer',
    };

    const prop = layerProps[kind];

    /** Remove every Isabela layer from the map so only one can exist at a time. */
    const removeAllIsabelaLayers = () => {
      (['isabelaPhilriceNDVILayer', 'isabelaPhilriceOrthoLayer'] as const).forEach((p) => {
        if (this[p]) {
          this.map.removeLayer(this[p]!);
          this[p] = null;
        }
      });
    };

    if (visible) {
      this.clearCaviteSilangLayers();
      removeAllIsabelaLayers();
      const tileUrl = buildIsabelaPhilriceTileUrl(kind);
      const layer = new TileLayer({
        source: new XYZ({
          url: tileUrl,
          crossOrigin: 'anonymous',
          tileSize: 512,
          tileLoadFunction: isabelaPhilriceRgbTileWhiteTransparent,
        }),
        visible: true,
        opacity,
        zIndex: 152,
      });
      if (kind === 'ndvi') {
        this.isabelaPhilriceNDVILayer = layer;
      } else {
        this.isabelaPhilriceOrthoLayer = layer;
      }
      this.map.addLayer(layer);
      this.map.render();
      return this.ISABELA_PHILRICE_BBOX;
    }

    if (this[prop]) {
      this.map.removeLayer(this[prop]!);
      this[prop] = null;
    }
    this.map.render();
    return null;
  }

  setIsabelaPhilriceLayerOpacity(kind: 'ndvi' | 'ortho', opacity: number) {
    const mapKind: Record<typeof kind, 'isabelaPhilriceNDVILayer' | 'isabelaPhilriceOrthoLayer'> = {
      ndvi: 'isabelaPhilriceNDVILayer',
      ortho: 'isabelaPhilriceOrthoLayer',
    };
    const L = this[mapKind[kind]];
    if (L) {
      L.setOpacity(opacity);
      this.map.render();
    }
  }

  clearIsabelaPhilriceLayers() {
    (['ndvi', 'ortho'] as const).forEach((k) => {
      this.toggleIsabelaPhilriceLayer(k, false, 1);
    });
  }

  /**
   * Cavite Silang COG extent — same footprint for NDVI + ortho.
   * From `/api/bounds`: crs http://www.opengis.net/def/crs/EPSG/0/4326
   */
  private readonly CAVITE_SILANG_BBOX: [number, number, number, number] = [
    120.98690150017501,
    14.242578462121,
    120.99130550646001,
    14.245994406611999,
  ];

  toggleCaviteSilangLayer(
    kind: 'ndvi' | 'ortho',
    visible: boolean,
    opacity: number = 1
  ): [number, number, number, number] | null {
    const layerProps: Record<'ndvi' | 'ortho', 'caviteSilangNDVILayer' | 'caviteSilangOrthoLayer'> = {
      ndvi: 'caviteSilangNDVILayer',
      ortho: 'caviteSilangOrthoLayer',
    };

    const prop = layerProps[kind];

    const removeAllCaviteSilangLayers = () => {
      (['caviteSilangNDVILayer', 'caviteSilangOrthoLayer'] as const).forEach((p) => {
        if (this[p]) {
          this.map.removeLayer(this[p]!);
          this[p] = null;
        }
      });
    };

    if (visible) {
      this.clearIsabelaPhilriceLayers();
      this.clearDroneSiteLayers();
      removeAllCaviteSilangLayers();
      const tileUrl = buildCaviteSilangTileUrl(kind);
      const layer = new TileLayer({
        source: new XYZ({
          url: tileUrl,
          crossOrigin: 'anonymous',
          tileSize: 512,
          tileLoadFunction: isabelaPhilriceRgbTileWhiteTransparent,
        }),
        visible: true,
        opacity,
        zIndex: 152,
      });
      if (kind === 'ndvi') {
        this.caviteSilangNDVILayer = layer;
      } else {
        this.caviteSilangOrthoLayer = layer;
      }
      this.map.addLayer(layer);
      this.map.render();
      return this.CAVITE_SILANG_BBOX;
    }

    if (this[prop]) {
      this.map.removeLayer(this[prop]!);
      this[prop] = null;
    }
    this.map.render();
    return null;
  }

  setCaviteSilangLayerOpacity(kind: 'ndvi' | 'ortho', opacity: number) {
    const mapKind: Record<typeof kind, 'caviteSilangNDVILayer' | 'caviteSilangOrthoLayer'> = {
      ndvi: 'caviteSilangNDVILayer',
      ortho: 'caviteSilangOrthoLayer',
    };
    const L = this[mapKind[kind]];
    if (L) {
      L.setOpacity(opacity);
      this.map.render();
    }
  }

  clearCaviteSilangLayers() {
    (['ndvi', 'ortho'] as const).forEach((k) => {
      this.toggleCaviteSilangLayer(k, false, 1);
    });
    // Callers use this as the "drop every drone layer" hook, so registry sites go too.
    this.clearDroneSiteLayers();
  }

  /** MapView subscribes so the panel state follows layers removed from anywhere. */
  setDroneSiteLayerListener(listener: ((activeLayerId: string | null) => void) | null) {
    this.droneSiteListener = listener;
  }

  /**
   * Show / hide one registry drone layer (`droneSites.ts`). Mutually exclusive with every other
   * drone layer (Isabela, Cavite and other registry layers). Returns the flight bbox when shown.
   */
  toggleDroneSiteLayer(
    layer: ResolvedDroneLayer,
    visible: boolean,
    opacity: number = 1
  ): [number, number, number, number] | null {
    if (!visible) {
      this.clearDroneSiteLayers();
      return null;
    }

    this.clearIsabelaPhilriceLayers();
    this.clearCaviteSilangLayers(); // also clears any registry layer
    const olLayer = new TileLayer({
      source: new XYZ({
        url: buildDroneLayerTileUrl(layer),
        crossOrigin: 'anonymous',
        tileSize: 512,
        // RGB tiles paint padding white; DEM uses `nodata` server-side so it needs no post-processing.
        ...(layer.kind === 'dem' ? {} : { tileLoadFunction: isabelaPhilriceRgbTileWhiteTransparent }),
      }),
      visible: true,
      opacity,
      zIndex: 152,
    });
    this.droneSiteLayers[layer.id] = olLayer;
    this.map.addLayer(olLayer);
    this.map.render();
    this.droneSiteListener?.(layer.id);
    return layer.flight.bbox;
  }

  setDroneSiteLayerOpacity(layerId: string, opacity: number) {
    const layer = this.droneSiteLayers[layerId];
    if (layer) {
      layer.setOpacity(opacity);
      this.map.render();
    }
  }

  clearDroneSiteLayers(notify: boolean = true) {
    const ids = Object.keys(this.droneSiteLayers);
    if (!ids.length) return;
    ids.forEach((id) => {
      this.map.removeLayer(this.droneSiteLayers[id]);
      delete this.droneSiteLayers[id];
    });
    this.map.render();
    if (notify) this.droneSiteListener?.(null);
  }

  /**
   * Show a single computed index colormap over the drone mission footprint.
   * Returns bbox for optional fit-to-extent.
   */
  setDroneOrthoIndexMap(
    tileUrl: string,
    bbox: [number, number, number, number],
    opacity: number = 1
  ): [number, number, number, number] {
    this.clearDroneOrthoIndexMap();
    const layer = new TileLayer({
      source: new XYZ({
        url: tileUrl,
        crossOrigin: 'anonymous',
        tileSize: 512,
        // Hide rectangular empty COG pad (pale yellow from rdylgn at index ≈ 0)
        tileLoadFunction: droneOrthoIndexTilePaddingTransparent,
      }),
      visible: true,
      opacity,
      zIndex: 155,
    });
    this.droneOrthoIndexLayer = layer;
    this.map.addLayer(layer);
    this.map.render();
    return bbox;
  }

  clearDroneOrthoIndexMap() {
    if (this.droneOrthoIndexLayer) {
      this.map.removeLayer(this.droneOrthoIndexLayer);
      this.droneOrthoIndexLayer = null;
      this.map.render();
    }
  }

  // UPDATED: Cleanup layers (includes crop area layers and drone NDVI)
  cleanup() {
    // Clean up analysis layers
    if (this.heatMapLayer) {
      this.map.removeLayer(this.heatMapLayer);
      this.heatMapLayer = null;
    }
    if (this.timeSeriesLayer) {
      this.map.removeLayer(this.timeSeriesLayer);
      this.timeSeriesLayer = null;
    }
    
    // Clean up WMS layers
    if (this.yieldLayer) {
      this.map.removeLayer(this.yieldLayer);
      this.yieldLayer = null;
    }
    
    // Clean up crop area layers
    if (this.maizeLayer) {
      this.map.removeLayer(this.maizeLayer);
      this.maizeLayer = null;
    }
    if (this.sugarCaneLayer) {
      this.map.removeLayer(this.sugarCaneLayer);
      this.sugarCaneLayer = null;
    }
    if (this.riceLayer) {
      this.map.removeLayer(this.riceLayer);
      this.riceLayer = null;
    }
    
    // Clean up rice forecast layer
    if (this.riceForecastLayer) {
      this.map.removeLayer(this.riceForecastLayer);
      this.riceForecastLayer = null;
    }

    // Clean up LST Philippines layer
    if (this.lstPHLayer) {
      this.map.removeLayer(this.lstPHLayer);
      this.lstPHLayer = null;
    }

    // Clean up BSRI layer
    if (this.bsriLayer) {
      this.map.removeLayer(this.bsriLayer);
      this.bsriLayer = null;
    }

    // Clean up Straw Burn layer
    if (this.strawBurnLayer) {
      this.map.removeLayer(this.strawBurnLayer);
      this.strawBurnLayer = null;
    }

    if (this.coconutLayer) {
      this.map.removeLayer(this.coconutLayer);
      this.coconutLayer = null;
    }

    // Clean up Planting Method Map layers
    if (this.plantingMethodDry2025Layer) {
      this.map.removeLayer(this.plantingMethodDry2025Layer);
      this.plantingMethodDry2025Layer = null;
    }
    if (this.plantingMethodDry2024Layer) {
      this.map.removeLayer(this.plantingMethodDry2024Layer);
      this.plantingMethodDry2024Layer = null;
    }
    if (this.riceGrowthStageLayer) {
      this.map.removeLayer(this.riceGrowthStageLayer);
      this.riceGrowthStageLayer = null;
    }
    if (this.irrigationTypeLayer) {
      this.map.removeLayer(this.irrigationTypeLayer);
      this.irrigationTypeLayer = null;
    }

    if (this.isabelaPhilriceNDVILayer) {
      this.map.removeLayer(this.isabelaPhilriceNDVILayer);
      this.isabelaPhilriceNDVILayer = null;
    }
    if (this.isabelaPhilriceOrthoLayer) {
      this.map.removeLayer(this.isabelaPhilriceOrthoLayer);
      this.isabelaPhilriceOrthoLayer = null;
    }

    if (this.caviteSilangNDVILayer) {
      this.map.removeLayer(this.caviteSilangNDVILayer);
      this.caviteSilangNDVILayer = null;
    }
    if (this.caviteSilangOrthoLayer) {
      this.map.removeLayer(this.caviteSilangOrthoLayer);
      this.caviteSilangOrthoLayer = null;
    }
    this.droneSiteListener = null;
    this.clearDroneSiteLayers(false);
    this.clearDroneOrthoIndexMap();

    this.clearFloodDamageRaster();

    // Clean up change detection layers
    this.clearChangeDetectionLayers();

    // Clean up vector layer
    if (this.vectorLayer) {
      this.map.removeLayer(this.vectorLayer);
      this.vectorLayer = null;
    }
    if (this.farmGroupLayer) {
      this.map.removeLayer(this.farmGroupLayer);
      this.farmGroupLayer = null;
    }
    if (this.farmClusterLayer) {
      this.map.removeLayer(this.farmClusterLayer);
      this.farmClusterLayer = null;
    }
  }

  showFloodDamageRaster(raster: FloodDamageRasterRef | null, options?: { fit?: boolean; showLayer?: boolean }) {
    const fit = options?.fit === true;
    const showLayer = options?.showLayer !== false;
    if (!raster?.url) {
      this.clearFloodDamageRaster();
      return;
    }

    if (!showLayer) {
      this.clearFloodDamageRaster();
      if (fit) this.fitFloodDamageRaster(raster);
      return;
    }

    if (this.floodDamageRasterUrl === raster.url && this.floodDamageRasterLayer) {
      if (fit) this.fitFloodDamageRaster(raster);
      return;
    }

    this.clearFloodDamageRaster();

    // These RapidDamage files are uint8 with GDAL nodata 255 filling the bbox.
    // Band 8 (pix_damage) is empty in every file — show baseline yield instead.
    const band = raster.displayBand && raster.displayBand !== 8 ? raster.displayBand : 3;
    const nodata = 255;
    const source = new GeoTIFF({
      sources: [
        {
          url: raster.url,
          bands: [band],
          nodata,
        },
      ],
      // Keep raw 0–255 so 255 can be hidden; normalize would map nodata to 1 (solid red).
      normalize: false,
      interpolate: false,
      convertToRGB: false,
      transition: 0,
    });

    this.floodDamageRasterLayer = new WebGLTileLayer({
      source,
      zIndex: 55,
      opacity: 1,
      style: {
        color: [
          'case',
          ['any', ['>=', ['band', 1], nodata], ['<=', ['band', 1], 0]],
          [0, 0, 0, 0],
          [
            'interpolate',
            ['linear'],
            ['band', 1],
            1, [255, 255, 178, 0.82],
            2, [254, 204, 92, 0.88],
            3, [253, 141, 60, 0.9],
            4, [240, 59, 32, 0.92],
            6, [189, 0, 38, 0.95],
          ],
        ],
      },
    });
    this.floodDamageRasterUrl = raster.url;
    this.map.addLayer(this.floodDamageRasterLayer);

    if (fit) this.fitFloodDamageRaster(raster);
  }

  private fitFloodDamageRaster(raster: FloodDamageRasterRef) {
    if (!raster.bbox || raster.bbox.length !== 4) return;
    const extent = transformExtent(raster.bbox, 'EPSG:4326', 'EPSG:3857');
    this.map.getView().fit(extent, {
      padding: [56, 56, 56, 56],
      duration: 500,
      maxZoom: 14,
    });
  }

  getFloodDamageLegendInfo(): typeof FLOOD_DAMAGE_RASTER_LEGEND | null {
    if (!this.floodDamageRasterLayer) return null;
    return FLOOD_DAMAGE_RASTER_LEGEND;
  }

  clearFloodDamageRaster() {
    if (this.floodDamageRasterLayer) {
      this.map.removeLayer(this.floodDamageRasterLayer);
      this.floodDamageRasterLayer = null;
    }
    this.floodDamageRasterUrl = null;
  }

  setFloodDamageRasterOpacity(opacity: number) {
    if (this.floodDamageRasterLayer) {
      this.floodDamageRasterLayer.setOpacity(opacity);
    }
  }

  // GeoServer philippines_flood_damage WMS removed — municipality RapidDamage GeoTIFFs are used instead
  toggleFloodDamageLayer(_visible: boolean, _wmsUrl?: string, _layerName?: string, _opacity: number = 1) {}

  setFloodDamageLayerOpacity(_opacity: number) {}

  getFloodDamageLegendUrl(_layerName: string): string {
    return '';
  }
}